import type { KeyboardEvent, ReactNode } from "react";
import { useState } from "react";
import { makeStyles, mergeClasses, tokens, Badge, Body1, Caption1, Card, Divider, Field, Menu, MenuDivider, MenuItem, MenuItemRadio, MenuList, MenuPopover, MenuTrigger, Radio, RadioGroup, Spinner, Tab, TabList } from "@fluentui/react-components";
import { CopyIconButton } from "@/components/copy-icon-button";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { DocDesignSamples } from "@/features/docs/DocDesignSamples";
import { DisplayTitle, PageHeaderTitle, SectionTitle } from "@/components/ui-typography";
import {
  Add16Regular,
  Add20Regular,
  Apps20Regular,
  ArrowUndo16Regular,
  Board16Regular,
  Board20Regular,
  Bookmark16Regular,
  CalendarLtr20Regular,
  CalendarMonth20Regular,
  ChevronLeft16Regular,
  ChevronRight16Regular,
  ClipboardCheckmark16Regular,
  Color16Regular,
  Color20Regular,
  DataPie20Regular,
  Delete16Regular,
  Diamond16Regular,
  Dismiss16Regular,
  ErrorCircle16Regular,
  Filter16Regular,
  Options16Regular,
  Flag16Regular,
  Home16Regular,
  Home20Regular,
  ArrowSort16Regular,
  Document16Regular,
  Folder16Regular,
  Image16Regular,
  Link16Regular,
  MoreHorizontal16Regular,
  MoreHorizontal20Regular,
  PanelRight16Regular,
  People16Regular,
  PeopleTeam16Regular,
  Search16Regular,
  Shield16Regular,
  Sparkle16Regular,
  TableSimple16Regular,
  TaskListSquareLtr20Regular,
  TextIndentIncrease16Regular,
  WeatherMoon16Regular,
  WeatherMoon20Regular,
  WeatherSunny20Regular,
  Cursor16Regular,
  HandRight16Regular,
  Note16Regular,
  Vote16Regular,
  Map16Regular,
  ZoomIn16Regular,
  ZoomOut16Regular,
  ArrowFit16Regular,
} from "@fluentui/react-icons";
import { AppDialog, ConfirmDialog, DialogActionsBar, DialogCheckbox, DialogField, DialogGrid2, DialogToggleField, executionTaskDeleteConfirmMessage, wbsTaskDeleteConfirmMessage } from "@/components/app-dialog";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import { MockCaseMenu } from "@/features/access/MockCaseMenu";
import { InviteDialog, InvitePanel } from "@/components/invite-dialog";
import { DateCalendar, DateField } from "@/components/date-field";
import { FuiButton, FuiDropdown, FuiFilterSelect, FuiInput, FuiSearchBox } from "@/components/fui-control";
import { FilterLabel, ListFilterMenu, ListSearch, ListToolbar, useListToolbarStyles } from "@/components/list-toolbar";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { BarProgressLabel, fitsProgressLabel } from "@/features/gantt/BarProgressLabel";
import { RemainingLabel } from "@/features/gantt/RemainingLabel";
import type { RemainingInfo } from "@/engine/remaining";
import { useDataTableStyles } from "@/components/data-table";
import { mmMonthCellStyle, usePortfolioStyles } from "@/features/resources/PortfolioViews";
import { fmtCurrency } from "@/lib/format";
import { DATE_PATTERN_SPECS, datePattern, formatAgo, formatDate, formatDateRange, formatDayHeading, formatDaysUntil, formatMonthDaySpan, weekdayNames } from "@/lib/date-format";
import { ResourceListTable } from "@/features/resources/ResourceListTable";
import type { OrganizationTeam, UserResource } from "@/engine/types";
import type { ResourceWorkloadSummary } from "@/store/catalog-store";
import { WorkCalendar } from "@/engine/calendar";
import { computeSchedule } from "@/engine/cpm";
import { todayISO } from "@/engine/date";
import { createSampleProject } from "@/engine/sample";
import { TaskCard } from "@/features/tasks/TaskCard";
import type { ExecutionPriority, ExecutionStatus } from "@/engine/types";
import { emptyFilter, type TaskFilter, type TaskQueryContext } from "@/features/tasks/task-query";
import { RecordStatusBadge, SeverityBadge, recordStatusOptions, severityOptions } from "@/features/records/record-ui";
import { AssigneeAvatars, DueChip, LABEL_STYLE, LabelChip, PRIORITY_VALUE_OPTIONS, PriorityBadge, ScopeChips, STATUS_VALUE_OPTIONS, StatusBadge, TaskPriorityChip, TaskStatusChip } from "@/features/tasks/task-ui";
import { TaskCommandMenu } from "@/features/tasks/TaskCommandMenu";
import { TaskFilterChips } from "@/features/tasks/task-filter-chips";
import { priorityGlyph, statusGlyph, taskFieldIcon } from "@/features/tasks/task-glyphs";
import { RECORD_KIND_LABEL, RECORD_KINDS, RECORD_STATUSES, SEVERITY_ORDER } from "@/store/record-ops";
import { cn } from "@/lib/utils";
import { ChartsTab } from "./design-system-charts";
import { InkTab } from "./design-system-ink";
import { FiltersTab } from "./design-system-filters";
import { WorkspaceMarkSpecimens } from "./design-system-workspace";
import { LABEL_COLORS, makeExecutionTask, PRIORITY_LABEL, PRIORITY_ORDER, STATUS_LABEL, STATUS_ORDER } from "@/store/execution-task-ops";

type DsTab = "foundations" | "dates" | "tables" | "filters" | "charts" | "charts2" | "icons" | "schedule" | "tasks" | "calendar";

const TABS: Array<{ value: DsTab; label: string }> = [
  { value: "foundations", label: "기초" },
  { value: "dates", label: "날짜 포맷" },
  { value: "tables", label: "표" },
  { value: "filters", label: "필터" },
  { value: "charts", label: "차트" },
  { value: "charts2", label: "차트2" },
  { value: "icons", label: "아이콘" },
  { value: "schedule", label: "일정" },
  { value: "tasks", label: "Task" },
  { value: "calendar", label: "캘린더" },
];

const useStyles = makeStyles({
  page: {
    height: "100%",
    overflow: "auto",
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalXXL}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  titles: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalL,
  },
  tabs: {
    marginBottom: tokens.spacingVerticalXL,
  },
  stack: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXL,
    maxWidth: "960px",
  },
  sectionHead: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  card: {
    padding: tokens.spacingHorizontalL,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  toolbarCard: {
    padding: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  toolbarNote: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    color: tokens.colorNeutralForeground3,
  },
  concept: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL} ${tokens.spacingVerticalS}`,
  },
  conceptLine: {
    color: tokens.colorNeutralForeground3,
    lineHeight: tokens.lineHeightBase300,
  },
  conceptName: {
    color: tokens.colorNeutralForeground1,
  },
  dockStage: {
    position: "relative",
    height: "168px",
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: "hidden",
  },
  dock: {
    position: "absolute",
    left: "50%",
    bottom: "16px",
    transform: "translateX(-50%)",
    display: "flex",
    alignItems: "center",
    gap: "2px",
    padding: "4px 6px",
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: "none",
  },
  dockRule: {
    width: "1px",
    height: "16px",
    marginLeft: "4px",
    marginRight: "4px",
    backgroundColor: tokens.colorNeutralStroke2,
    flexShrink: 0,
  },
  dockButton: {
    width: "28px",
    minWidth: "28px",
    height: "28px",
    minHeight: "28px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  dockButtonOn: {
    backgroundColor: tokens.colorNeutralForeground1,
    color: tokens.colorNeutralBackground1,
    ":hover": {
      backgroundColor: tokens.colorNeutralForeground1Hover,
      color: tokens.colorNeutralBackground1,
    },
  },
  dockZoom: {
    minWidth: "40px",
    textAlign: "center",
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
    fontVariantNumeric: "tabular-nums",
  },
  dialogShell: {
    display: "flex",
    gap: "12px",
    minHeight: "180px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: "12px",
  },
  dialogTabs: {
    flex: "0 0 140px",
    gap: "4px",
  },
  dialogTab: {
    justifyContent: "flex-start",
    width: "100%",
    minHeight: "32px",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "10px",
    borderRadius: tokens.borderRadiusMedium,
    fontWeight: tokens.fontWeightRegular,
    "& .fui-Tab__content": {
      fontWeight: tokens.fontWeightRegular,
      fontSize: tokens.fontSizeBase300,
      lineHeight: tokens.lineHeightBase300,
      whiteSpace: "nowrap",
    },
    "&::before, &::after": {
      content: "none",
    },
    "&[aria-selected='true']": {
      backgroundColor: tokens.colorNeutralBackground3,
    },
    "&[aria-selected='true']:hover": {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  dialogPane: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    paddingTop: "4px",
    paddingLeft: "8px",
  },
  center: { textAlign: "center" },
  quiet: { color: tokens.colorNeutralForeground3 },
  menuBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  row: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  glyphGroups: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
    gap: tokens.spacingHorizontalS,
  },
  swatchGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
    gap: tokens.spacingHorizontalS,
  },
  dateField: {
    width: "280px",
  },
  dialogFormLayout: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: tokens.spacingHorizontalXL,
  },
  specimenFrame: {
    maxHeight: "360px",
  },
  dialogFormPreview: {
    width: "448px",
    maxWidth: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: tokens.spacingHorizontalL,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  dialogChrome: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "8px",
    marginLeft: "-16px",
    marginRight: "-16px",
    marginTop: "-16px",
    paddingLeft: "16px",
    paddingRight: "12px",
    paddingTop: "8px",
    paddingBottom: "4px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  dialogChromeTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    minWidth: 0,
    flex: "1 1 auto",
    color: tokens.colorNeutralForeground3,
  },
  dialogChromeTitle: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  dialogChromeActions: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    flexShrink: 0,
    marginLeft: "auto",
    color: tokens.colorNeutralForeground3,
  },
  dialogChromeBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
  },
  dialogChromeFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    marginLeft: "-16px",
    marginRight: "-16px",
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingTop: "8px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground3,
  },
});

export function DesignSystemPage() {
  const styles = useStyles();
  const [tab, setTab] = useState<DsTab>("foundations");
  return (
    <div className={styles.page}>
      <div className={styles.titles}>
        <DisplayTitle>디자인 시스템</DisplayTitle>
        <Body1>페이지 CTA는 28px, 다이얼로그 액션은 24px. 화면에서 쓰는 아이콘·메타포·컨트롤을 한곳에 둡니다.</Body1>
      </div>
      <TabList
        className={styles.tabs}
        selectedValue={tab}
        onTabSelect={(_, d) => {
          if (typeof d.value === "string") setTab(d.value as DsTab);
        }}
        aria-label="구획"
      >
        {TABS.map((t) => (
          <Tab key={t.value} value={t.value}>
            {t.label}
          </Tab>
        ))}
      </TabList>
      <div className={styles.stack} role="tabpanel">
        {tab === "foundations" && <FoundationsTab />}
        {tab === "dates" && <DateFormatTab />}
        {tab === "tables" && <TablesTab />}
        {tab === "filters" && <FiltersTab />}
        {tab === "charts" && <ChartsTab />}
        {tab === "charts2" && <InkTab />}
        {tab === "icons" && <IconsTab />}
        {tab === "schedule" && <ScheduleTab />}
        {tab === "tasks" && <PlannerTab />}
        {tab === "calendar" && <CalendarTab />}
      </div>
    </div>
  );
}

function CanvasDockDemo() {
  const styles = useStyles();
  const [tool, setTool] = useState("select");
  const [minimap, setMinimap] = useState(true);
  const tools = [
    { id: "select", label: "선택", icon: <Cursor16Regular /> },
    { id: "hand", label: "이동", icon: <HandRight16Regular /> },
    { id: "add", label: "추가", icon: <Add16Regular /> },
    { id: "link", label: "연결", icon: <Link16Regular /> },
    { id: "note", label: "메모", icon: <Note16Regular /> },
    { id: "vote", label: "투표", icon: <Vote16Regular /> },
  ];
  return (
    <Section title="캔버스 독" hint="도구와 화면은 아래 한 줄. 선택만 검정 채움, 나머지는 진한 아이콘. 도움말·목록은 헤더.">
      <Card className={styles.card}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3, lineHeight: 1.6 }}>
          왼쪽 레일을 두지 않습니다. 28px 아이콘, Regular, 1px 테두리, 그림자 없음. 도구와 확대·맞추기·미니맵은 세로 선으로 나눕니다.
        </Caption1>
        <div className={styles.dockStage} aria-label="캔버스 독 예시">
          <div className={styles.dock} role="toolbar" aria-label="캔버스 도구">
            {tools.map((item) => (
              <button
                key={item.id}
                type="button"
                aria-label={item.label}
                aria-pressed={tool === item.id}
                className={mergeClasses(styles.dockButton, tool === item.id && styles.dockButtonOn)}
                onClick={() => setTool(item.id)}
              >
                {item.icon}
              </button>
            ))}
            <span className={styles.dockRule} />
            <button type="button" aria-label="축소" className={styles.dockButton}>
              <ZoomOut16Regular />
            </button>
            <span className={styles.dockZoom}>100%</span>
            <button type="button" aria-label="확대" className={styles.dockButton}>
              <ZoomIn16Regular />
            </button>
            <button type="button" aria-label="화면 맞추기" className={styles.dockButton}>
              <ArrowFit16Regular />
            </button>
            <button
              type="button"
              aria-label={minimap ? "미니맵 숨기기" : "미니맵 보기"}
              aria-pressed={minimap}
              className={mergeClasses(styles.dockButton, minimap && styles.dockButtonOn)}
              onClick={() => setMinimap((on) => !on)}
            >
              <Map16Regular />
            </button>
          </div>
        </div>
      </Card>
    </Section>
  );
}

function FoundationsTab() {
  const styles = useStyles();
  const [demoDropdownVal, setDemoDropdownVal] = useState("team");
  const [demoFilterVal1, setDemoFilterVal1] = useState("team");
  const [demoFilterVal2, setDemoFilterVal2] = useState("all");
  const [demoPlanView, setDemoPlanView] = useState("grid");
  const [directorySection, setDirectorySection] = useState("members");
  const [directoryRole, setDirectoryRole] = useState("all");
  const [directoryQuery, setDirectoryQuery] = useState("");
  const [confirmDemo, setConfirmDemo] = useState<"wbs" | "task" | "team" | null>(null);

  return (
    <>
      <Section title="타이포 & 명도 위계" hint="기본 Regular(400) 고수 · 3단계 텍스트 명도로 위계 구분">
        <Card className={styles.card}>
          <div style={{ display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS }}>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>타이포그래피 스케일 (모두 Regular 400)</Caption1>
            <DisplayTitle>DisplayTitle (20px) · 플랜명·핵심 메트릭</DisplayTitle>
            <PageHeaderTitle>PageHeaderTitle (16px) · 페이지 상단 헤더</PageHeaderTitle>
            <SectionTitle>SectionTitle (15px) · 섹션·그룹 제목</SectionTitle>
            <Body1>Body1 (13px) · 본문 및 테이블 셀 표준 폰트</Body1>
            <Caption1>Caption1 (12px) · 보조 라벨, 메타데이터, 2줄 셀의 보조 역할</Caption1>
          </div>
          <Divider />
          <div style={{ display: "flex", flexDirection: "column", gap: tokens.spacingVerticalXS }}>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>3단계 텍스트 명도 대비 (굵기 대신 명도로 중요도 구분)</Caption1>
            <div style={{ color: tokens.colorNeutralForeground1 }}>Primary Text (colorNeutralForeground1) — 주요 본문, 작업 이름, 핵심 데이터</div>
            <div style={{ color: tokens.colorNeutralForeground2 }}>Secondary Text (colorNeutralForeground2) — 보조 설명, 카테고리, 덜 강조되는 정보</div>
            <div style={{ color: tokens.colorNeutralForeground3 }}>Tertiary Text (colorNeutralForeground3) — 메타데이터, 작성일, 비활성 힌트</div>
          </div>
        </Card>
      </Section>
      <Section title="문서" hint="목록 행, 연결 칩, 구분. 에디터 전체는 두지 않습니다.">
        <Card className={styles.card}>
          <DocDesignSamples />
        </Card>
      </Section>
      <Section title="컴포넌트 7대 상태" hint="모든 대화형 요소가 지원해야 할 라이프사이클 상태 명세">
        <Card className={styles.card}>
          <Caption1 style={{ color: tokens.colorNeutralForeground3, lineHeight: 1.6 }}>
            버튼 및 입력 필드의 7대 상태 예시. 키보드 탐색(Tab) 시 <code>focus-visible</code> 포커스 링이 선명히 드러납니다.
          </Caption1>
          <div className={styles.row} style={{ gap: tokens.spacingHorizontalM, flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>1. Default</Caption1>
              <FuiButton appearance="primary">기본 버튼</FuiButton>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>2. Hover</Caption1>
              <FuiButton appearance="secondary">호버 피드백</FuiButton>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>3. Focus-visible</Caption1>
              <FuiButton appearance="outline" className="focus-ring">포커스 링</FuiButton>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>4. Active</Caption1>
              <FuiButton appearance="subtle">눌림</FuiButton>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>5. Disabled</Caption1>
              <FuiButton appearance="primary" disabled>비활성화</FuiButton>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>6. Loading</Caption1>
              <FuiButton appearance="primary" icon={<Spinner size="extra-small" />}>처리 중</FuiButton>
            </div>
          </div>
          <div className={styles.row} style={{ gap: tokens.spacingHorizontalM, flexWrap: "wrap", marginTop: tokens.spacingVerticalS }}>
            <Field label="7. Error (유효성 검사 실패)" validationState="error" validationMessage="필수 입력 항목입니다.">
              <FuiInput defaultValue="잘못된 값" />
            </Field>
            <Field label="Disabled Input">
              <FuiInput disabled defaultValue="수정 불가 항목" />
            </Field>
          </div>
        </Card>
      </Section>
      <Section title="복사" hint="값 바로 오른쪽의 20px 아이콘. 누르면 복사하고 잠시 체크가 된다.">
        <Card className={styles.card}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "2px" }}>
            <Caption1>lim.growth@wavex.io</Caption1>
            <CopyIconButton value="lim.growth@wavex.io" label="이메일 복사" />
          </div>
        </Card>
      </Section>
      <Section title="색" hint="중성 회색 · 액센트 1색 · 신호 1색">
        <div className={styles.swatchGrid}>
          <ColorTile name="background" className="bg-background" />
          <ColorTile name="card" className="bg-card" />
          <ColorTile name="muted" className="bg-muted" />
          <ColorTile name="foreground" className="bg-foreground" />
          <ColorTile name="accent / tint" className="bg-tint" />
          <ColorTile name="critical" className="bg-critical" />
          <ColorTile name="bar" className="bg-bar" />
          <ColorTile name="bar-summary" className="bg-bar-summary" />
          <ColorTile name="bar-baseline" className="bg-bar-baseline" />
          <ColorTile name="bar-label" className="bg-bar-label" />
        </div>
      </Section>
      <Section title="빈 상태" hint="화면·메뉴의 목록이 통째로 비면 고스트. 그 안의 작은 칸은 보조색 한 줄. 고스트는 툴바 아래 남은 높이의 세로 가운데. 카드 안 CTA는 두지 않음">
        <div className={styles.row} style={{ alignItems: "stretch" }}>
          <Card className={styles.card} style={{ height: 280, flex: 1 }}>
            <GhostEmptyState />
          </Card>
          <Card className={styles.card} style={{ height: 280, flex: 1 }}>
            <GhostEmptyState variant="search" />
          </Card>
          <Card className={styles.card} style={{ flex: 1, alignSelf: "flex-start" }}>
            <Caption1 style={{ display: "block", padding: "12px", textAlign: "center", color: tokens.colorNeutralForeground3 }}>팀원이 없습니다</Caption1>
          </Card>
        </div>
      </Section>
      <Section title="삭제 확인" hint="제목과 본문은 정적 동작. 고른 항목의 이름은 넣지 않는다">
        <Card className={styles.card}>
          <Body1>취소와 빨간 삭제. 확인 제목과 본문은 동작과 부수 효과만 적고, 고른 항목의 이름·사람·날짜·역할은 넣지 않습니다. 예: 팀을 삭제할까요? 소속된 사람의 팀은 비워지고, 인력 기록은 남습니다.</Body1>
          <div className={styles.row}>
            <FuiButton appearance="outline" onClick={() => setConfirmDemo("wbs")}>
              작업 삭제 예시
            </FuiButton>
            <FuiButton appearance="outline" onClick={() => setConfirmDemo("task")}>
              Task 삭제 예시
            </FuiButton>
            <FuiButton appearance="outline" onClick={() => setConfirmDemo("team")}>
              팀 삭제 예시
            </FuiButton>
          </div>
        </Card>
        <ConfirmDialog
          open={confirmDemo === "wbs"}
          title="작업 삭제"
          message={wbsTaskDeleteConfirmMessage(1)}
          onCancel={() => setConfirmDemo(null)}
          onConfirm={() => setConfirmDemo(null)}
        />
        <ConfirmDialog
          open={confirmDemo === "task"}
          title="Task 삭제"
          message={executionTaskDeleteConfirmMessage(3)}
          onCancel={() => setConfirmDemo(null)}
          onConfirm={() => setConfirmDemo(null)}
        />
        <ConfirmDialog
          open={confirmDemo === "team"}
          title="팀 삭제"
          message="팀을 삭제할까요? 소속된 사람의 팀은 비워지고, 인력 기록은 남습니다."
          onCancel={() => setConfirmDemo(null)}
          onConfirm={() => setConfirmDemo(null)}
        />
      </Section>
      <Section title="버튼" hint="PRD §8.6 컨트롤 높이 — 28px 기본, 24px 소형">
        <Card className={styles.card}>
          <Caption1>페이지 CTA · 28px</Caption1>
          <div className={styles.row}>
            <FuiButton appearance="primary" icon={<Add16Regular />}>
              첫 플랜 만들기
            </FuiButton>
            <FuiButton appearance="primary">만들기</FuiButton>
            <FuiButton appearance="secondary">취소</FuiButton>
            <FuiButton appearance="subtle" icon={<Home16Regular />}>
              홈
            </FuiButton>
            <FuiButton appearance="subtle" icon={<WeatherMoon16Regular />} />
            <FuiButton appearance="subtle" icon={<Color16Regular />} />
          </div>
          <Caption1>다이얼로그 · 24px</Caption1>
          <div className={styles.row}>
            <FuiButton density="dialog" appearance="primary">
              계속
            </FuiButton>
            <FuiButton density="dialog" appearance="primary" icon={<ChevronRight16Regular />} iconPosition="after">
              다음
            </FuiButton>
            <FuiButton density="dialog" appearance="secondary">
              취소
            </FuiButton>
            <FuiButton density="dialog" appearance="subtle" icon={<ChevronLeft16Regular />}>
              이전
            </FuiButton>
            <FuiButton density="dialog" appearance="subtle">
              건너뛰기
            </FuiButton>
            <FuiButton density="dialog" appearance="subtle" tone="danger" icon={<Delete16Regular />}>
              팀 삭제
            </FuiButton>
            <FuiButton density="dialog" appearance="primary">
              적용
            </FuiButton>
          </div>
        </Card>
      </Section>
      <CanvasDockDemo />
      <Section title="폼" hint="입력은 페이지와 같이 28px, 다이얼로그는 24px">
        <Card className={styles.card}>
          <Caption1>텍스트 입력 · 28px</Caption1>
          <Field label="플랜 이름">
            <FuiInput defaultValue="새 플랜" />
          </Field>
          <Caption1>검색 입력 (SearchBox) · 페이지 28px 기본, 다이얼로그 24px</Caption1>
          <div className={styles.row}>
            <FuiSearchBox placeholder="제목·플랜·라벨 검색 (페이지 · 28px)" style={{ width: 260 }} />
            <FuiSearchBox density="dialog" placeholder="다이얼로그 검색 (24px)" style={{ width: 220 }} />
          </div>
          <Caption1>폼 한 줄. 목록 화면의 검색 자리는 아래 「목록 툴바」를 따른다</Caption1>
          <div className={styles.row}>
            <FuiDropdown
              style={{ width: 140 }}
              value="all"
              options={[
                { value: "all", label: "모든 담당" },
                { value: "unassigned", label: "담당 미정" },
              ]}
              onValueChange={() => {}}
            />
            <FuiSearchBox placeholder="제목·플랜·라벨 검색" style={{ width: 220 }} />
            <FuiButton appearance="primary">검색</FuiButton>
          </div>
          <Field label="템플릿">
            <RadioGroup defaultValue="sample">
              <Radio value="sample" label="ERP 구축 샘플 — 약 40개 작업으로 시연" />
              <Radio value="empty" label="빈 플랜 — 작업 하나부터" />
            </RadioGroup>
          </Field>
        </Card>
      </Section>
      <Section title="다이얼로그 폼" hint="헤더는 제목, 제목 오른쪽 …, 구분선, 오른쪽 끝 닫기. 예산안 변경이 이 구조를 따릅니다">
        <DialogFormDemo />
      </Section>
      <Section title="초대" hint="이번에 고른 사람만 목록에 있습니다. 권한은 그 자리에서 바꾸고, 빼기는 … 메뉴에 있습니다. 수락 전 초대는 워크스페이스 설정의 초대에서 봅니다.">
        <InviteDemo />
      </Section>
      <Section
        title="셀렉트 & 필터 컨트롤"
        hint="폼 입력용 표준 드롭다운(접두사 지원)과 툴바/필터 바 전용 위계형 셀렉트 비교"
      >
        <Card className={styles.card}>
          {/* 컴포넌트 A: FuiDropdown (prefix 지원) */}
          <div style={{ display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS }}>
            <Body1>방안 A. FuiDropdown (접두사 prefix 지원 표준 드롭다운)</Body1>
            <Caption1 style={{ color: tokens.colorNeutralForeground3, lineHeight: 1.6 }}>
              <strong>• 스타일:</strong> Fluent UI 표준 드롭다운 외형(28px 페이지 높이 / 24px 다이얼로그 높이)을 유지합니다.<br />
              <strong>• 동작 및 특징:</strong> <code>prefix</code> 속성을 전달하면 닫힌 상태의 버튼에는 &apos;[접두사]: [선택값]&apos; 형태로 결합하여 표시하지만, 팝오버 목록에서는 접두사를 자동으로 제거하여 순수 옵션명만 표시합니다. 팝오버는 드롭다운보다 좁아지지 않고, 옵션이 더 길면 그 길이만큼 넓어집니다.<br />
              <strong>• 추천 용도:</strong> 일반 입력 폼, 설정 모달, 테이블 셀 등 표준적인 콤보박스/드롭다운 형태가 어울리는 영역.
            </Caption1>
            <div className={styles.row} style={{ alignItems: "center" }}>
              <FuiDropdown
                style={{ width: 140 }}
                prefix="묶기"
                value={demoDropdownVal}
                options={[
                  { value: "none", label: "없음" },
                  { value: "team", label: "팀" },
                  { value: "project", label: "플랜" },
                  { value: "grade", label: "등급" },
                ]}
                onValueChange={setDemoDropdownVal}
              />
              <Caption1 style={{ color: tokens.colorNeutralForeground4 }}>
                ← 클릭 시 팝오버 목록에는 접두사 없이 &apos;없음, 팀, 플랜, 등급&apos;만 깔끔하게 표시됨
              </Caption1>
            </div>
          </div>

          <Divider />

          {/* 컴포넌트 B: FuiFilterSelect (툴바 전용 위계형 셀렉트) */}
          <div style={{ display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Body1>방안 B. FuiFilterSelect (툴바 전용 시각적 위계형 셀렉트)</Body1>
              <Badge appearance="tint" color="brand" size="small">
                실제 적용 컴포넌트
              </Badge>
            </div>
            <Caption1 style={{ color: tokens.colorNeutralForeground3, lineHeight: 1.6 }}>
              <strong>• 스타일:</strong> 툴바 및 필터 바 전용 28px 셀렉트. 버튼 내부에서 라벨(접두사)은 은은한 보조색(<code>colorNeutralForeground3</code>, 12px)으로 배치하고, 얇은 세로선 뒤의 선택값은 또렷한 색상(<code>colorNeutralForeground1</code>, Regular 400)으로 표현하여 <strong>시각적 위계(Visual Hierarchy)</strong>를 극대화합니다.<br />
              <strong>• 동작 및 특징:</strong> 팝오버 메뉴에서 접두사 없는 순수 값 목록과 현재 선택 상태를 나타내는 Fluent UI 표준 라디오 체크 아이콘이 정갈하게 표시됩니다. 팝오버는 셀렉트보다 좁아지지 않고, 옵션이 더 길면 그 길이만큼 넓어집니다.<br />
              <strong>• 추천 용도:</strong> 상단 툴바, 인력/작업 필터 바, 정렬 바 등 사용자가 메타데이터 기준을 신속하게 파악하고 변경해야 하는 컨트롤 바.
            </Caption1>
            <div className={styles.row} style={{ alignItems: "center", gap: tokens.spacingHorizontalM, flexWrap: "wrap" }}>
              <FuiFilterSelect
                label="묶기"
                value={demoFilterVal1}
                options={[
                  { value: "none", label: "없음" },
                  { value: "team", label: "팀" },
                  { value: "project", label: "플랜" },
                  { value: "grade", label: "등급" },
                ]}
                onValueChange={setDemoFilterVal1}
              />
              <FuiFilterSelect
                label="필터"
                value={demoFilterVal2}
                options={[
                  { value: "all", label: "전체" },
                  { value: "available", label: "투입 가능" },
                  { value: "over", label: "과부하 (1)" },
                  { value: "external", label: "프리·외주" },
                ]}
                onValueChange={setDemoFilterVal2}
              />
              <FuiFilterSelect
                density="dialog"
                label="정렬"
                value="name"
                options={[
                  { value: "name", label: "이름순" },
                  { value: "rate", label: "단가순" },
                ]}
                onValueChange={() => {}}
              />
              <Caption1 style={{ color: tokens.colorNeutralForeground4 }}>
                ← 라벨(보조색)과 선택값(강조색) 분리 및 팝오버 라디오 체크 제공
              </Caption1>
            </div>
          </div>
        </Card>
      </Section>
      <Section title="세그먼트 탭" hint="SegmentedControl — 같은 화면의 보기, 또는 한 표의 범위. 사이즈 변형 없음">
        <Card className={styles.card}>
          <Caption1 style={{ color: tokens.colorNeutralForeground3, lineHeight: 1.6 }}>
            플랜의 카드·목록, 리소스의 목록·보드처럼 같은 목록의 보기는 도구 줄 오른쪽이다. 멤버의 역할, 플랜의 상태처럼 한 표의 범위도 이 컨트롤이고, 그때는 검색 옆이다. 목록의 대상을 가르는 자리는 아래 「상위·하위 탭」의 밑줄 탭이다. 아이템 높이 24px, 외곽 약 29px. 사이즈 변형 없음.
          </Caption1>
          <div className={styles.row} style={{ alignItems: "center" }}>
            <SegmentedControl
              value={demoPlanView}
              onChange={setDemoPlanView}
              ariaLabel="플랜 보기"
              options={[
                { value: "grid", label: "카드" },
                { value: "list", label: "목록" },
              ]}
            />
          </div>
        </Card>
      </Section>
      <Section title="상위·하위 탭" hint="대상은 제목 아래 밑줄 탭. 그 탭의 같은 표만 검색 옆 세그먼트로 좁힌다.">
        <Card className={styles.toolbarCard}>
          <div className={styles.concept}>
            <Caption1 className={styles.conceptLine}>
              <span className={styles.conceptName}>상위. </span>
              멤버·팀·권한처럼 목록의 대상이 갈리면 제목 아래 밑줄 탭이다. 탭을 바꾸면 본문, 본동사, 검색이 그 대상으로 바뀐다. 고른 탭만 Medium이다. 검색어는 비운다.
            </Caption1>
            <Caption1 className={styles.conceptLine}>
              <span className={styles.conceptName}>하위. </span>
              멤버의 역할은 같은 사람 표의 범위다. 전체·소유자·관리자·편집자·뷰어는 검색 옆 세그먼트다. 팀과 권한에는 그 표가 없으므로 세그먼트가 없다. 멤버로 돌아오면 고른 역할은 그대로다.
            </Caption1>
            <Caption1 className={styles.conceptLine}>
              <span className={styles.conceptName}>예산. </span>
              솔루션·구조·기간은 밑줄 탭이다. 솔루션의 상태(전체·운영·계약·예정·종료)는 세그먼트이고 분류는 필터다. 구조에는 세그먼트가 없다. 기간의 월·분기는 눈금이다.
            </Caption1>
            <Caption1 className={styles.conceptLine}>
              <span className={styles.conceptName}>문서. </span>
              전체·문서·회의록·메모는 밑줄 탭이다. 공개 범위는 세그먼트이고, 작성과 연결은 한 필터이고, 정렬은 그 메뉴의 하위다. 휴지통은 도구 줄 오른쪽이다. 종류를 바꿔도 공개 범위는 남고, 검색어는 비운다. 본동사는 문서 추가이고, 탭을 바꿔도 그대로다. 꺽쇠는 문서·회의록·메모이고, 본 버튼은 문서를 만든다. 워크스페이스 문서도 같은 탭·세그먼트·필터다. 플랜이 둘 이상이면 플랜은 필터다. 한 플랜만 고르면 플랜 열은 숨긴다. 설명은 문서 수·비공개 수이고, 비공개가 없으면 그 말은 뺀다. 플랜이 둘 이상이면 플랜 수를 더한다. 문서 추가와 휴지통은 플랜 목록에만 둔다.
            </Caption1>
            <Caption1 className={styles.conceptLine}>
              <span className={styles.conceptName}>파일. </span>
              전체·이미지·파일·링크는 밑줄 탭이다. 출처는 세그먼트이고 처음은 전체다. 종류를 바꿔도 출처는 남고, 검색어는 비운다. 플랜이 둘 이상이면 필터다. 설명은 파일 수·이미지·파일·링크이고, 없는 말은 뺀다. 플랜이 둘 이상이면 플랜 수를 더한다. 제목 옆 …는 목업 사례다.
            </Caption1>
            <Caption1 className={styles.conceptLine}>
              <span className={styles.conceptName}>작업. </span>
              목록·보드·분석은 밑줄 탭이다. 목록과 보드의 상태는 세그먼트이고 처음은 미완료다. 우선순위·마감·담당은 필터다. 분석에는 세그먼트가 없다. 보기를 바꿔도 고른 상태는 남고, 검색어는 비운다. 내 작업은 보드 하나다. 같은 상태 범위이고 열은 오른쪽이다.
            </Caption1>
            <Caption1 className={styles.conceptLine}>
              <span className={styles.conceptName}>홈. </span>
              마감·플랜·일정은 밑줄 탭이다. 마감의 이번 주·지연만 세그먼트이고, 처음은 이번 주다. 이번 주가 없고 지연이 있으면 지연부터 연다. 플랜은 전체 목록이고 상태 열은 없다. 일정은 달 이동 없이 전체 목록이다. 검색은 없다. 탭을 바꿔도 고른 마감 범위는 남는다. 설명은 참여·미완료이고, 이번 주·지연·주의가 없으면 그 말은 뺀다. 본동사는 플랜의 플랜 추가다. 제목 옆 …는 목업 사례이고, 많이·적게·지연·기준 안·없음·초기화다.
            </Caption1>
            <Caption1 className={styles.conceptLine}>
              <span className={styles.conceptName}>활동. </span>
              밑줄 탭은 없다. 검색은 없다. 플랜이 둘 이상이면 플랜은 필터 앞의 선택이고 기본은 모든 플랜이다. 한 플랜이거나 플랜 안이면 그 선택은 없다. 필터는 종류·사람이고, 지연만은 그 메뉴에 있다. 기본 종류는 작업·이슈·일정이고, 문서·기준선은 꺼져 있다. 설명은 오늘 건수·지연이고, 오늘이 없으면 최근 건수다. 지연이 없으면 그 말은 뺀다. 본동사는 없다. 제목 옆 …는 목업 사례이고, 많이·오늘·최근·없음·초기화다.
            </Caption1>
            <Caption1 className={styles.conceptLine}>
              <span className={styles.conceptName}>주간 보고. </span>
              밑줄 탭은 없다. 제목 옆 …는 목업 사례이고, 없음·현재 데이터다. 목업 플랜은 기준일을 포함한 주간 보고를 두세 개 갖고 있다.
            </Caption1>
            <Caption1 className={styles.conceptLine}>
              <span className={styles.conceptName}>플랜. </span>
              상태는 세그먼트이고 처음은 전체다. 정렬은 그다음이다. 카드·목록은 도구 줄 오른쪽이고 기본은 목록이다. 설명은 플랜 수·진행·완료·지연·아카이브이고, 없는 말은 뺀다. 제목 옆 …는 목업 사례이고, 없음·많이·현재 데이터다. 많이는 제목이 긴 플랜이 많다.
            </Caption1>
            <Caption1 className={styles.conceptLine}>
              <span className={styles.conceptName}>간격. </span>
              도구 줄이 있으면 그 줄이 밑줄과 표를 가른다. 권한처럼 도구 줄이 없으면 밑줄 아래에 8px를 둔다. 간격은 툴바가 둔다.
            </Caption1>
            <Caption1 className={styles.conceptLine}>
              <span className={styles.conceptName}>질문. </span>
              밑줄 탭은 무엇을 보는가이고, 세그먼트는 그 안에서 어디인가이다. 대화상자에서는 같은 대상 구분이 왼쪽 세로 탭이다.
            </Caption1>
          </div>
          <ListToolbar
            title="멤버"
            description="8명 · 팀 5개 · 초대 1건"
            headingAction={
              directorySection === "roles" ? undefined : (
                <FuiButton appearance="primary">{directorySection === "teams" ? "팀 추가" : "멤버 초대"}</FuiButton>
              )
            }
            sections={
              <TabList
                size="small"
                selectedValue={directorySection}
                aria-label="멤버 구성"
                onTabSelect={(_, data) => {
                  if (typeof data.value === "string") {
                    setDirectorySection(data.value);
                    setDirectoryQuery("");
                  }
                }}
              >
                <Tab value="members" icon={<People16Regular />}>멤버</Tab>
                <Tab value="teams" icon={<PeopleTeam16Regular />}>팀</Tab>
                <Tab value="roles" icon={<Shield16Regular />}>권한</Tab>
              </TabList>
            }
            search={directorySection === "members" ? <ListSearch value={directoryQuery} onChange={setDirectoryQuery} /> : undefined}
            filters={
              directorySection === "members" ? (
                <SegmentedControl
                  ariaLabel="역할"
                  value={directoryRole}
                  onChange={setDirectoryRole}
                  options={[
                    { value: "all", label: "전체" },
                    { value: "owner", label: "소유자" },
                    { value: "admin", label: "관리자" },
                    { value: "editor", label: "편집자" },
                    { value: "viewer", label: "뷰어" },
                  ]}
                />
              ) : undefined
            }
          />
          <Caption1 className={styles.toolbarNote}>
            {directorySection === "teams"
              ? "지금 팀. 팀 목록이다. 본동사는 팀 추가이고, 검색과 세그먼트는 없다."
              : directorySection === "roles"
                ? "지금 권한. 역할이 할 수 있는 일이다. 본동사와 검색은 없고, 밑줄과 표 사이는 8px다."
                : "지금 멤버. 사람 표를 역할로 좁힌다. 본동사는 멤버 초대다."}
          </Caption1>
        </Card>
      </Section>
      <Section title="목록 툴바" hint="일정만 빼고 20px 제목과 한 줄 설명이다. 설명에 개수는 없다. 본동사는 제목 오른쪽이다. 검색·필터·정렬은 왼쪽, 열과 보기 탭은 오른쪽. 필터는 아이콘과 글자. 한 축은 필터: 현재 값. 여러 축은 필터: 개수이고 개수는 회색 라운드 칩. 항목을 열어 고르고, 적용되면 맨 아래에 필터 초기화.">
        <ListToolbarDemo />
      </Section>
      <DialogSectionDemo />
    </>
  );
}

function DialogSectionDemo() {
  const styles = useStyles();
  const [section, setSection] = useState("members");
  const [scope, setScope] = useState("members");
  return (
    <Section title="대화상자 섹션" hint="내용 전체는 왼쪽 세로 탭. 같은 표의 범위만 툴바 세그먼트">
      <Card className={styles.card}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3, lineHeight: 1.6 }}>
          멤버와 공유처럼 내용이 통째로 갈리면 왼쪽 탭입니다. 페이지의 밑줄 탭과 같은 구분입니다. 멤버·초대·접근 요청처럼 같은 표의 범위만 툴바 세그먼트입니다. 세그먼트를 두 줄로 쌓지 않고, 같은 목록은 한 이름으로 부릅니다.
        </Caption1>
        <div className={styles.dialogShell}>
          <TabList
            className={styles.dialogTabs}
            vertical
            size="small"
            selectedValue={section}
            aria-label="공유 항목"
            onTabSelect={(_, data) => {
              if (typeof data.value === "string") setSection(data.value);
            }}
          >
            <Tab className={styles.dialogTab} value="members">멤버</Tab>
            <Tab className={styles.dialogTab} value="share">공유</Tab>
          </TabList>
          <div className={styles.dialogPane}>
            {section === "members" ? (
              <ListToolbar
                edge="table"
                filters={
                  <SegmentedControl
                    ariaLabel="멤버 목록"
                    value={scope}
                    onChange={setScope}
                    options={[
                      { value: "members", label: "멤버" },
                      { value: "invites", label: "초대" },
                      { value: "requests", label: "접근 요청 2" },
                    ]}
                  />
                }
                trail={<FuiButton appearance="primary" density="dialog">멤버 초대</FuiButton>}
              />
            ) : (
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                공유 목록에는 범위 세그먼트를 두지 않습니다. 상태 필터와 본동사만 있습니다.
              </Caption1>
            )}
          </div>
        </div>
      </Card>
    </Section>
  );
}

function DemoTaskFilter({
  filter,
  count,
  onChange,
}: {
  filter: TaskFilter;
  count: number;
  onChange: (next: TaskFilter) => void;
}) {
  const toolbar = useListToolbarStyles();
  const [section, setSection] = useState<null | "status" | "assignee">(null);
  const statusId = filter.statuses[0];
  const assigneeId = filter.assignees[0];
  const statusHint = statusId == null ? undefined : statusId === "unset" ? "상태 미정" : STATUS_LABEL[statusId];
  const assigneeHint = assigneeId === "" ? "담당 없음" : assigneeId;
  const goBack = () => setSection(null);
  return (
    <Menu
      checkedValues={{
        status: statusId != null ? [statusId] : ["all"],
        assignee: assigneeId != null ? [assigneeId] : ["all"],
      }}
      onCheckedValueChange={(_, data) => {
        const next = data.checkedItems[0];
        if (data.name === "status") onChange({ ...filter, statuses: !next || next === "all" ? [] : [next as ExecutionStatus] });
        if (data.name === "assignee") onChange({ ...filter, assignees: !next || next === "all" ? [] : [next] });
      }}
      onOpenChange={(_, data) => {
        if (!data.open) setSection(null);
      }}
    >
      <MenuTrigger disableButtonEnhancement>
        <FuiButton appearance="subtle" icon={<Filter16Regular />} aria-label={count > 0 ? `필터: ${count}` : "필터"}>
          <FilterLabel count={count} />
        </FuiButton>
      </MenuTrigger>
      <MenuPopover>
        <MenuList aria-label="필터">
          {section == null ? (
            <>
              <MenuItem
                persistOnClick
                submenuIndicator={<ChevronRight16Regular />}
                secondaryContent={statusHint ? <span className={toolbar.menuHint}>{statusHint}</span> : undefined}
                onClick={() => setSection("status")}
              >
                상태
              </MenuItem>
              <MenuItem
                persistOnClick
                submenuIndicator={<ChevronRight16Regular />}
                secondaryContent={assigneeHint ? <span className={toolbar.menuHint}>{assigneeHint}</span> : undefined}
                onClick={() => setSection("assignee")}
              >
                담당
              </MenuItem>
              {count > 0 ? (
                <>
                  <MenuDivider />
                  <MenuItem icon={<Dismiss16Regular />} onClick={() => onChange(emptyFilter())}>
                    필터 초기화
                  </MenuItem>
                </>
              ) : null}
            </>
          ) : (
            <>
              <MenuItem persistOnClick icon={<ChevronLeft16Regular />} onClick={goBack}>
                {section === "status" ? "상태" : "담당"}
              </MenuItem>
              <MenuDivider />
              {section === "status" ? (
                <>
                  <MenuItemRadio name="status" value="all">전체</MenuItemRadio>
                  {STATUS_ORDER.map((status) => (
                    <MenuItemRadio key={status} name="status" value={status}>
                      {STATUS_LABEL[status]}
                    </MenuItemRadio>
                  ))}
                </>
              ) : (
                <>
                  <MenuItemRadio name="assignee" value="all">전체</MenuItemRadio>
                  <MenuItemRadio name="assignee" value="나">나</MenuItemRadio>
                </>
              )}
            </>
          )}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

function ListToolbarDemo() {
  const styles = useStyles();
  const [kind, setKind] = useState("all");
  const [phase, setPhase] = useState("all");
  const [fileKind, setFileKind] = useState("all");
  const [fileSource, setFileSource] = useState("all");
  const [filePlan, setFilePlan] = useState("all");
  const [fileSort, setFileSort] = useState("recent");
  const [fileCase, setFileCase] = useState("live");
  const [query, setQuery] = useState("");
  const [fileQuery, setFileQuery] = useState("");
  const [taskQuery, setTaskQuery] = useState("");
  const fileScale =
    fileCase === "many"
      ? "48개 · 이미지 16 · 파일 16 · 링크 16 · 4개 플랜"
      : fileCase === "few"
        ? "3개 · 이미지 1 · 파일 1 · 링크 1"
        : fileCase === "byPlan"
          ? "26개 · 이미지 11 · 파일 9 · 링크 6 · 4개 플랜"
          : fileCase === "empty"
            ? "0개"
            : "12개 · 이미지 4 · 파일 6 · 링크 2 · 3개 플랜";
  const [view, setView] = useState("list");
  const [taskFilter, setTaskFilter] = useState<TaskFilter>({ ...emptyFilter(), statuses: ["inProgress"], assignees: ["나"] });
  const chipCtx = {
    today: "2026-10-21",
    schedule: null,
    project: { tasks: [], planner: { buckets: [], labels: [], customFields: [] } },
  } as unknown as TaskQueryContext;
  const taskFilterCount =
    taskFilter.statuses.length +
    taskFilter.priorities.length +
    taskFilter.assignees.length +
    taskFilter.bucketIds.length +
    taskFilter.labelIds.length +
    taskFilter.linkedTaskIds.length +
    (taskFilter.due !== "any" ? 1 : 0) +
    (taskFilter.undecidedOnly ? 1 : 0);
  return (
    <Card className={styles.toolbarCard}>
      <Caption1 className={styles.toolbarNote}>이슈. 검색과 처리 상태는 왼쪽, 종류 탭은 오른쪽. 한 축은 필터: 현재 값이고, 기본이 아니면 메뉴 아래에 필터 초기화.</Caption1>
      <ListToolbar
        title="이슈"
        description="12건 · 처리 필요 4 · 기한 지남 1"
        search={<ListSearch value={query} onChange={setQuery} />}
        filters={
          <ListFilterMenu
            label="처리 상태"
            value={phase}
            idleValue="all"
            onChange={setPhase}
            options={[
              { value: "all", label: "전체" },
              { value: "open", label: "처리 필요" },
              { value: "active", label: "유효" },
              { value: "closed", label: "종결" },
            ]}
          />
        }
        view={
          <SegmentedControl
            value={kind}
            onChange={setKind}
            ariaLabel="기록 종류"
            options={[
              { value: "all", label: "전체" },
              { value: "issue", label: "이슈" },
              { value: "risk", label: "리스크" },
            ]}
          />
        }
      />
      <Caption1 className={styles.toolbarNote}>파일. 전체·이미지·파일·링크는 밑줄 탭. 출처는 세그먼트. 플랜이 둘 이상이면 필터. 설명은 규모. 제목 옆 …는 목업 사례.</Caption1>
      <ListToolbar
        title="파일"
        description={fileScale}
        titleAside={
          <Menu
            checkedValues={{ case: fileCase === "live" ? [] : [fileCase] }}
            onCheckedValueChange={(_, data) => {
              const next = data.checkedItems[0];
              if (next === "many" || next === "few" || next === "byPlan" || next === "empty") setFileCase(next);
            }}
            positioning="below-start"
          >
            <MenuTrigger disableButtonEnhancement>
              <FuiButton appearance="subtle" icon={<MoreHorizontal16Regular />} aria-label="목업 사례" />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItemRadio name="case" value="many">많이</MenuItemRadio>
                <MenuItemRadio name="case" value="few">적게</MenuItemRadio>
                <MenuItemRadio name="case" value="byPlan">플랜별</MenuItemRadio>
                <MenuItemRadio name="case" value="empty">없음</MenuItemRadio>
                <MenuDivider />
                <MenuItem onClick={() => setFileCase("live")}>초기화</MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        }
        sections={
          <TabList
            size="small"
            selectedValue={fileKind}
            aria-label="파일"
            onTabSelect={(_, data) => {
              if (typeof data.value === "string") {
                setFileKind(data.value);
                setFileQuery("");
              }
            }}
          >
            <Tab value="all" icon={<Folder16Regular />}>전체</Tab>
            <Tab value="image" icon={<Image16Regular />}>이미지</Tab>
            <Tab value="file" icon={<Document16Regular />}>파일</Tab>
            <Tab value="link" icon={<Link16Regular />}>링크</Tab>
          </TabList>
        }
        search={<ListSearch value={fileQuery} onChange={setFileQuery} />}
        filters={
          <>
            <SegmentedControl
              value={fileSource}
              onChange={setFileSource}
              ariaLabel="파일 출처"
              options={[
                { value: "all", label: "전체" },
                { value: "task", label: "작업" },
                { value: "record", label: "기록" },
                { value: "document", label: "문서" },
                { value: "comment", label: "댓글" },
              ]}
            />
            <FuiFilterSelect
              label="플랜"
              ariaLabel="플랜"
              value={filePlan}
              options={[
                { value: "all", label: "모든 플랜" },
                { value: "marketing", label: "고객 마케팅 시스템" },
                { value: "launch", label: "제품 출시" },
              ]}
              onValueChange={setFilePlan}
            />
            <Menu
              checkedValues={{ sort: [fileSort] }}
              onCheckedValueChange={(_, data) => {
                const next = data.checkedItems[0];
                if (next) setFileSort(next);
              }}
            >
              <MenuTrigger disableButtonEnhancement>
                <FuiButton appearance="subtle" icon={<ArrowSort16Regular />}>
                  {fileSort === "name" ? "이름" : fileSort === "size" ? "크기" : "등록일"}
                </FuiButton>
              </MenuTrigger>
              <MenuPopover>
                <MenuList aria-label="정렬">
                  <MenuItemRadio name="sort" value="recent">등록일</MenuItemRadio>
                  <MenuItemRadio name="sort" value="name">이름</MenuItemRadio>
                  <MenuItemRadio name="sort" value="size">크기</MenuItemRadio>
                </MenuList>
              </MenuPopover>
            </Menu>
          </>
        }
      />
      <Caption1 className={styles.toolbarNote}>작업. 제목은 20px, 오른쪽이 본동사. 정렬과 미정은 필터를 열어 고른다. 여러 축은 항목을 연다. 개수는 회색 라운드 칩. 조건은 아래 칩.</Caption1>
      <ListToolbar
        title="작업"
        description="이 플랜의 담당, 마감, 진행"
        headingAction={
          <FuiButton appearance="primary" icon={<Add16Regular />}>
            Task 추가
          </FuiButton>
        }
        search={<ListSearch value={taskQuery} onChange={setTaskQuery} label="Task 검색" placeholder="Task 검색" />}
        filters={
          <>
            <DemoTaskFilter filter={taskFilter} count={taskFilterCount} onChange={setTaskFilter} />
          </>
        }
        trail={
          <FuiButton appearance="subtle" icon={<Options16Regular />} aria-label="열">
            열
          </FuiButton>
        }
        view={
          <SegmentedControl
            value={view}
            onChange={setView}
            ariaLabel="작업 보기"
            options={[
              { value: "list", icon: <TableSimple16Regular />, label: "목록" },
              { value: "board", icon: <Board16Regular />, label: "보드" },
            ]}
          />
        }
      />
      <TaskFilterChips filter={taskFilter} ctx={chipCtx} onChange={setTaskFilter} />
      <Caption1 className={styles.toolbarNote}>줄을 고르면 화면 보기·검색·새 Task는 숨기고 일괄 편집만 둡니다.</Caption1>
      <ListToolbar
        selecting
        filters={
          <>
            <Body1>1개 선택</Body1>
            <FuiButton appearance="outline" icon={taskFieldIcon("status")}>상태</FuiButton>
            <FuiButton appearance="outline" icon={taskFieldIcon("priority")}>우선순위</FuiButton>
            <FuiButton appearance="outline" icon={taskFieldIcon("bucket")}>버킷</FuiButton>
            <FuiButton appearance="outline" icon={taskFieldIcon("due")}>날짜 지우기</FuiButton>
            <FuiButton appearance="outline" icon={<Delete16Regular />}>
              삭제
            </FuiButton>
            <FuiButton appearance="subtle" icon={<Dismiss16Regular />}>
              선택 해제
            </FuiButton>
          </>
        }
      />
    </Card>
  );
}

function TablesTab() {
  return (
    <>
      <TableTypesDemo />
      <RowActionDemo />
      <TableItemsDemo />
    </>
  );
}

function RowActionDemo() {
  const styles = useStyles();
  const t = useDataTableStyles();
  const [notice, setNotice] = useState("… 를 열면 그 줄의 동작이 있습니다. 동작이 없는 줄은 — 입니다.");
  return (
    <Section title="동작 열" hint="동사는 … 메뉴. 없으면 —">
      <Card className={styles.card}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3, lineHeight: 1.6 }}>
          제거·중지·취소는 동작이 하나여도 셀에 버튼을 두지 않습니다. 지우거나 되돌리기 어려운 항목은 메뉴 다음 확인이고, 확인 본문에는 그 줄의 이름을 넣지 않습니다.
        </Caption1>
        <div className={t.frame}>
          <table className={t.table} aria-label="동작 열">
            <thead>
              <tr>
                <th className={t.th}>이름</th>
                <th className={mergeClasses(t.th, styles.center)} style={{ width: 88 }}>상태</th>
                <th className={mergeClasses(t.th, styles.center)} style={{ width: 72 }}>동작</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={t.td}>윤요청</td>
                <td className={mergeClasses(t.td, styles.center)}>요청 중</td>
                <td className={mergeClasses(t.td, styles.center)}>
                  <Menu>
                    <MenuTrigger disableButtonEnhancement>
                      <button type="button" className={styles.menuBtn} aria-label="윤요청 동작">
                        <MoreHorizontal16Regular />
                      </button>
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        <MenuItem onClick={() => setNotice("「윤요청」의 접근 요청 취소를 확인합니다.")}>취소</MenuItem>
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </td>
              </tr>
              <tr>
                <td className={t.td}>나</td>
                <td className={mergeClasses(t.td, styles.center)}>소유자</td>
                <td className={mergeClasses(t.td, styles.center)}>
                  <Caption1 className={styles.quiet}>—</Caption1>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{notice}</Caption1>
      </Card>
    </Section>
  );
}

function TableTypesDemo() {
  const styles = useStyles();
  const [empty, setEmpty] = useState(false);
  const [notice, setNotice] = useState("행을 누르면 프로필, 이름 오른쪽 더보기는 호버·포커스 때 나타납니다.");
  return (
    <Section title="표 및 데이터 그리드 표준" hint="목록은 DataGrid · 헤더 Regular 400 · 숫자 우측 정렬 · 그룹은 본문 행">
      <Card className={styles.card}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3, lineHeight: 1.6 }}>
          인력·작업처럼 정렬하고 고르는 목록은 이 표를 표준으로 씁니다. 외곽선은 없고, 헤더와 본문은 13px Regular·같은 글자색입니다. 헤더는 옅은 면과 아래 선으로만 구분합니다. 가동률·단가는 우측 정렬과 고정폭 숫자입니다. 월별 M/M처럼 열이 데이터에 따라 늘어나는 표도 같은 헤더를 쓰고, 선택 열은 일괄 동작이 있을 때만 둡니다.
        </Caption1>
        <SegmentedControl
          ariaLabel="표 표본"
          value={empty ? "empty" : "list"}
          onChange={(value) => setEmpty(value === "empty")}
          options={[
            { value: "list", label: "목록" },
            { value: "empty", label: "빈 목록" },
          ]}
        />
        {empty ? (
          <GhostEmptyState variant="search" title="조건에 맞는 인력이 없습니다" description="검색어나 필터를 바꿔 보세요." />
        ) : (
          <ResourceListTable
            className={styles.specimenFrame}
            resources={SPECIMEN_PEOPLE}
            teams={SPECIMEN_TEAMS}
            teamOrder={SPECIMEN_TEAM_ORDER}
            workloads={SPECIMEN_WORKLOADS}
            groupBy="team"
            masked={false}
            onOpen={(resource) => setNotice(`${resource.displayName} 프로필`)}
            onEdit={(resource) => setNotice(`${resource.displayName} 수정`)}
            onDelete={(resource) => setNotice(`${resource.displayName} 삭제`)}
            onOpenProject={(projectId) => setNotice(`${projectId} 플랜으로 이동`)}
          />
        )}
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{notice}</Caption1>
      </Card>
    </Section>
  );
}

function TableItemsDemo() {
  const styles = useStyles();
  const t = useDataTableStyles();
  const p = usePortfolioStyles();
  const [notice, setNotice] = useState("월 보기에서 셀을 누르면 그 달의 투입 일정을 고칩니다.");
  const openCell = (message: string) => setNotice(message);
  const onCellKey = (e: KeyboardEvent<HTMLTableCellElement>, message: string) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openCell(message);
    }
  };
  return (
    <>
      <Section title="항목 종류" hint="식별은 두 줄 · 숫자는 오른쪽 · 둘째 줄만 보조색">
        <Card className={styles.card}>
          <Caption1 style={{ color: tokens.colorNeutralForeground3, lineHeight: 1.6 }}>
            목록과 매트릭스가 같은 셀을 씁니다. 이름과 제목은 첫 줄이 본문이고 둘째 줄만 보조색입니다. 금액, M/M, 비율, 날짜는 오른쪽 정렬과 고정폭 숫자입니다. 사람 프로필은 이름 글자가 아니라 사람 아이콘입니다. 상태, 우선순위, 기록 상태, 심각도는 12px 표식과 본문 글자이고, 색은 표식에만 둡니다. 우선순위나 심각도가 없으면 「—」입니다.
          </Caption1>
          <div className={t.frame}>
            <table className={t.table} aria-label="표 항목 종류">
              <thead>
                <tr>
                  <th className={t.th} style={{ width: 88 }}>종류</th>
                  <th className={t.th}>셀</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={t.td}>식별</td>
                  <td className={mergeClasses(t.td, t.identityCell)}>
                    <span className={t.person}>
                      <PersonAvatar name="한로봇" size={28} tip={false} />
                      <span className={t.personText}>
                        <span className={t.nameLine}>
                          <span className={mergeClasses(t.clip, t.personName)}>한로봇</span>
                        </span>
                        <Caption1 className={t.role}>프로젝트 리드</Caption1>
                      </span>
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className={t.td}>제목</td>
                  <td className={t.td}>
                    <span className={t.twoLine}>
                      <span className={t.clip}>로그인 실패가 반복됩니다</span>
                      <Caption1 className={t.role}>재현 절차 첫 줄이 여기에 붙습니다</Caption1>
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className={t.td}>텍스트</td>
                  <td className={t.td}>
                    <span className={t.clip}>코어엔진팀</span>
                  </td>
                </tr>
                <tr>
                  <td className={t.td}>상태</td>
                  <td className={t.td}>
                    <span className={styles.row}>
                      <TaskStatusChip status={undefined} />
                      {STATUS_ORDER.map((status) => (
                        <TaskStatusChip key={status} status={status} />
                      ))}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className={t.td}>우선순위</td>
                  <td className={t.td}>
                    <span className={styles.row}>
                      <TaskPriorityChip priority={undefined} />
                      {PRIORITY_ORDER.map((priority) => (
                        <TaskPriorityChip key={priority} priority={priority} />
                      ))}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className={t.td}>기록 상태</td>
                  <td className={t.td}>
                    <span className={styles.glyphGroups}>
                      {RECORD_KINDS.map((kind) => (
                        <span key={kind} className={styles.row}>
                          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{RECORD_KIND_LABEL[kind]}</Caption1>
                          {RECORD_STATUSES[kind].map((status) => (
                            <RecordStatusBadge key={status} kind={kind} status={status} />
                          ))}
                        </span>
                      ))}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className={t.td}>심각도</td>
                  <td className={t.td}>
                    <span className={styles.row}>
                      <SeverityBadge severity={undefined} />
                      {SEVERITY_ORDER.map((severity) => (
                        <SeverityBadge key={severity} severity={severity} />
                      ))}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className={t.td}>뱃지</td>
                  <td className={t.td}>
                    <Badge appearance="tint" color="brand">진행 중</Badge>
                  </td>
                </tr>
                <tr>
                  <td className={t.td}>링크</td>
                  <td className={t.td}>
                    <button type="button" className={t.linkBtn} onClick={() => setNotice("알파 플랜으로 이동")}>
                      알파 플랜
                    </button>
                  </td>
                </tr>
                <tr>
                  <td className={t.td}>숫자</td>
                  <td className={mergeClasses(t.td, t.num)}>{fmtCurrency(11500000)} · 2.4 · 80% · 2026. 3. 1.</td>
                </tr>
                <tr className={t.total}>
                  <td className={t.td}>합계</td>
                  <td className={mergeClasses(t.td, t.num)}>{fmtCurrency(440000000)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      </Section>
      <Section title="편집 셀" hint="팀 월 M/M · 쉬고 있을 때는 숫자 · 호버와 포커스에서만 고칠 수 있음">
        <Card className={styles.card}>
          <Caption1 style={{ color: tokens.colorNeutralForeground3, lineHeight: 1.6 }}>
            월 보기에서만 버튼입니다. 클릭하거나 Enter로 그 달의 투입 일정을 고칩니다. 수동으로 덮어쓴 값은 아래 액센트 선, 기간 밖은 가운데 점입니다. 분기 열과 합계 행은 버튼이 아닙니다.
          </Caption1>
          <div className={t.frame}>
            <table className={t.table} aria-label="편집 셀 상태">
              <thead>
                <tr>
                  {["기본", "호버", "포커스", "수동", "기간 밖"].map((label) => (
                    <th key={label} className={mergeClasses(t.th, p.mmCol)} style={{ width: 72, minWidth: 72, maxWidth: 72 }}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <EditSample label="3월" value={0.6} onOpen={openCell} onKey={onCellKey} />
                  <EditSample label="4월" value={0.8} shown="hover" onOpen={openCell} onKey={onCellKey} />
                  <EditSample label="5월" value={1} shown="focus" onOpen={openCell} onKey={onCellKey} />
                  <EditSample label="6월" value={1} overridden onOpen={openCell} onKey={onCellKey} />
                  <EditSample label="7월" value={0} outside onOpen={openCell} onKey={onCellKey} />
                </tr>
              </tbody>
            </table>
          </div>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{notice}</Caption1>
        </Card>
      </Section>
    </>
  );
}

function EditSample({
  label,
  value,
  shown,
  overridden,
  outside,
  onOpen,
  onKey,
}: {
  label: string;
  value: number;
  shown?: "hover" | "focus";
  overridden?: boolean;
  outside?: boolean;
  onOpen: (message: string) => void;
  onKey: (e: KeyboardEvent<HTMLTableCellElement>, message: string) => void;
}) {
  const t = useDataTableStyles();
  const p = usePortfolioStyles();
  const text = outside || value <= 0 ? "·" : value.toFixed(1);
  const message = outside ? `${label} 기간에 넣기` : `${label} ${text} M/M${overridden ? " · 수동" : ""}`;
  return (
    <td
      className={mergeClasses(
        t.td,
        p.mmCol,
        t.editCell,
        overridden && t.editCellOverridden,
        shown === "hover" && t.editCellShownHover,
        shown === "focus" && t.editCellShownFocus,
      )}
      style={{ ...mmMonthCellStyle(outside ? 0 : value, 1), width: 72, minWidth: 72, maxWidth: 72 }}
      role="button"
      tabIndex={0}
      aria-label={message}
      onClick={() => onOpen(message)}
      onKeyDown={(e) => onKey(e, message)}
    >
      {text}
    </td>
  );
}

const SPECIMEN_TEAM_ORDER: OrganizationTeam[] = [
  { id: "core", name: "코어엔진팀", color: "#0f6cbd", description: "플랫폼" },
  { id: "ui", name: "UI플랫폼팀", color: "#107c10" },
];

const SPECIMEN_TEAMS: Record<string, OrganizationTeam> = Object.fromEntries(SPECIMEN_TEAM_ORDER.map((team) => [team.id, team]));

function specimenPerson(partial: Pick<UserResource, "id" | "displayName" | "roleTitle" | "teamId" | "employmentType" | "grade" | "defaultMonthlyRate">): UserResource {
  return {
    email: "",
    skills: [],
    isActive: true,
    ...partial,
  };
}

const SPECIMEN_PEOPLE: UserResource[] = [
  specimenPerson({ id: "han", displayName: "한로봇", roleTitle: "프로젝트 리드", teamId: "core", employmentType: "permanent", grade: "principal", defaultMonthlyRate: 14000000 }),
  specimenPerson({ id: "lee", displayName: "이지원", roleTitle: "시니어 프론트엔드", teamId: "ui", employmentType: "permanent", grade: "senior", defaultMonthlyRate: 11500000 }),
  specimenPerson({ id: "park", displayName: "박서연", roleTitle: "백엔드", teamId: "core", employmentType: "contract", grade: "mid", defaultMonthlyRate: 9000000 }),
  specimenPerson({ id: "choi", displayName: "최민재", roleTitle: "데이터", teamId: "ui", employmentType: "freelance", grade: "senior", defaultMonthlyRate: 12000000 }),
];

function specimenLoad(resourceId: string, ratio: number, assignments: ResourceWorkloadSummary["assignments"]): ResourceWorkloadSummary {
  return {
    resourceId,
    totalAllocationRatio: ratio,
    totalPlannedMM: ratio * 4,
    projectCount: assignments.length,
    isOverAllocated: ratio > 1.05,
    assignments,
  };
}

const SPECIMEN_WORKLOADS: Record<string, ResourceWorkloadSummary> = {
  han: specimenLoad("han", 1, [
    { projectId: "alpha", projectName: "알파 플랜", projectRole: "PL", allocationRatio: 0.6, plannedMM: 2.4, startDate: "2026-01-01", endDate: "2026-06-30" },
    { projectId: "beta", projectName: "베타 플랜", projectRole: "자문", allocationRatio: 0.4, plannedMM: 1.6, startDate: "2026-03-01", endDate: "2026-08-31" },
  ]),
  lee: specimenLoad("lee", 1.2, [
    { projectId: "alpha", projectName: "알파 플랜", projectRole: "개발", allocationRatio: 1.2, plannedMM: 4.8, startDate: "2026-01-01", endDate: "2026-06-30" },
  ]),
  park: specimenLoad("park", 0.5, []),
  choi: specimenLoad("choi", 0.8, [
    { projectId: "beta", projectName: "베타 플랜", projectRole: "개발", allocationRatio: 0.8, plannedMM: 3.2, startDate: "2026-02-01", endDate: "2026-07-31" },
  ]),
};

function IconsTab() {
  const styles = useStyles();
  return (
    <>
      <Section title="워크스페이스 표식" hint="올린 이미지가 있으면 그 면이다. 없으면 색 타일 위의 아이콘이다. 스위처는 소유와 참여를 나누고 오른쪽에 역할을 둔다.">
        <WorkspaceMarkSpecimens />
      </Section>
      <Section title="탐색" hint="사이드 네비와 페이지 액션">
        <div className={styles.grid}>
          <IconTile icon={<Home20Regular />} label="홈" />
          <IconTile icon={<TaskListSquareLtr20Regular />} label="내 작업 · 작업 탭" />
          <IconTile icon={<Apps20Regular />} label="플랜" />
          <IconTile icon={<CalendarMonth20Regular />} label="캘린더 탭" />
          <IconTile icon={<DataPie20Regular />} label="분석 탭" />
          <IconTile icon={<CalendarLtr20Regular />} label="작업 일정(레이어)" />
          <IconTile icon={<Add20Regular />} label="새 플랜" />
          <IconTile icon={<Board20Regular />} label="작업 보드 뷰" />
          <IconTile icon={<WeatherMoon20Regular />} label="다크" />
          <IconTile icon={<WeatherSunny20Regular />} label="라이트" />
          <IconTile icon={<Color20Regular />} label="디자인 시스템" />
          <IconTile icon={<MoreHorizontal20Regular />} label="더보기" />
          <IconTile icon={<ChevronLeft16Regular />} label="이전" />
          <IconTile icon={<ChevronRight16Regular />} label="다음" />
        </div>
      </Section>
      <Section title="일정 도구" hint="툴바·인스펙터·인사이트">
        <div className={styles.grid}>
          <IconTile icon={<Search16Regular />} label="검색" />
          <IconTile icon={<ArrowUndo16Regular />} label="실행 취소" />
          <IconTile icon={<TextIndentIncrease16Regular />} label="들여쓰기" />
          <IconTile icon={<Link16Regular />} label="관계" />
          <IconTile icon={<Flag16Regular />} label="기준선" />
          <IconTile icon={<Bookmark16Regular />} label="체크포인트" />
          <IconTile icon={<ClipboardCheckmark16Regular />} label="진척 업데이트" />
          <IconTile icon={<Delete16Regular />} label="삭제" />
          <IconTile icon={<PanelRight16Regular />} label="사이드 패널" />
          <IconTile icon={<Diamond16Regular />} label="마일스톤" />
          <IconTile icon={<Sparkle16Regular />} label="인사이트" />
          <IconTile icon={<ErrorCircle16Regular />} label="경고" />
        </div>
      </Section>
    </>
  );
}

function ScheduleTab() {
  const styles = useStyles();
  return (
    <>
    <Section title="간트 메타포" hint="채움은 진척, 빨강은 여유 없는 작업. 색은 중성·액센트·크리티컬 3계열만">
      <div className={styles.swatchGrid}>
        <Metaphor label="작업 바" hint="일반 일정 · 16px, 안쪽 진척률">
          <DemoTaskBar percent={40} />
        </Metaphor>
        <Metaphor label="여유 없는 작업" hint="크리티컬 패스 · 안쪽 진척률">
          <DemoTaskBar percent={60} critical />
        </Metaphor>
        <Metaphor label="요약" hint="단계 브래킷 · 왼쪽 남은 기간">
          <DemoSummaryBar info={{ kind: "active", days: 12 }} />
        </Metaphor>
        <Metaphor label="마일스톤" hint="다이아몬드">
          <div className="flex h-3.5 items-center justify-center">
            <div className="size-2.5 rotate-45 rounded-[1px] border-[1.5px] border-foreground/70 bg-card" />
          </div>
        </Metaphor>
        <Metaphor label="기준선" hint="처음 계획">
          <div className="h-1.5 w-full rounded-[1px] bg-bar-baseline" />
        </Metaphor>
        <Metaphor label="여유" hint="논크리티컬 연장">
          <div className="flex h-3.5 items-center">
            <div className="h-3.5 w-16 rounded-[3px] bg-bar" />
            <div className="h-0.5 flex-1 rounded-r-[2px] bg-bar/45" />
          </div>
        </Metaphor>
        <Metaphor label="선택" hint="액센트 8%">
          <div className="h-7 w-full rounded-sm bg-tint/10 ring-1 ring-tint" />
        </Metaphor>
        <Metaphor label="오늘·진척 기준일" hint="세로 기준선">
          <div className="relative h-7 w-full rounded-sm bg-card">
            <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-muted-foreground/50" />
          </div>
        </Metaphor>
      </div>
    </Section>
    <Section title="막대 안 진척률" hint="11px 레귤러·고정폭 숫자, 막대 왼쪽 4px 안쪽. 기본 노출이며 보기 메뉴·설정에서 끌 수 있음">
      <div className={styles.swatchGrid}>
        <Metaphor label="시작 전" hint="0%">
          <DemoTaskBar percent={0} />
        </Metaphor>
        <Metaphor label="진행 중" hint="20%">
          <DemoTaskBar percent={20} />
        </Metaphor>
        <Metaphor label="절반 이상" hint="65%">
          <DemoTaskBar percent={65} />
        </Metaphor>
        <Metaphor label="완료" hint="100%">
          <DemoTaskBar percent={100} />
        </Metaphor>
        <Metaphor label="여유 없는 작업" hint="빨간 띠만큼 글자를 더 들여씀">
          <DemoTaskBar percent={45} critical />
        </Metaphor>
        <Metaphor label="짧은 막대" hint="폭 36px 미만이면 숨김">
          <DemoTaskBar percent={50} width={28} />
        </Metaphor>
        <Metaphor label="촘촘한 행" hint="막대 높이 14px 미만(12px)이면 숨김">
          <DemoTaskBar percent={50} height={12} />
        </Metaphor>
        <Metaphor label="옵션 끔" hint="채움만 표시">
          <DemoTaskBar percent={50} showLabel={false} />
        </Metaphor>
      </div>
    </Section>
    <Section title="단계 남은 기간" hint="단계 브래킷 왼쪽 바깥 4px, 11px 레귤러·고정폭 숫자. 보기 메뉴·설정에서 끌 수 있음">
      <div className={styles.swatchGrid}>
        <Metaphor label="시작 전" hint="기준일 < 시작일 · 보조색">
          <DemoSummaryBar info={{ kind: "upcoming", days: 20 }} />
        </Metaphor>
        <Metaphor label="진행 중" hint="기준일 ≤ 종료일 · 본문색">
          <DemoSummaryBar info={{ kind: "active", days: 12 }} />
        </Metaphor>
        <Metaphor label="종료일 당일" hint="0d · 오늘까지 끝내야 함">
          <DemoSummaryBar info={{ kind: "active", days: 0 }} />
        </Metaphor>
        <Metaphor label="늦어짐" hint="종료일 지남 + 미완료 · 빨강 음수">
          <DemoSummaryBar info={{ kind: "overdue", days: -3 }} />
        </Metaphor>
        <Metaphor label="완료" hint="100% · 숫자 숨김">
          <DemoSummaryBar info={{ kind: "done", days: 0 }} />
        </Metaphor>
      </div>
    </Section>
    <RemainingPolicy />
    </>
  );
}

/** Mirrors the Gantt summary bracket plus the remaining-days label on its left. */
function DemoSummaryBar({ info }: { info: RemainingInfo }) {
  return (
    <div className="flex w-full items-center">
      <RemainingLabel info={info} className="pr-1" />
      <div className="relative h-[9px] flex-1">
        <div className="absolute inset-x-0 top-0 h-[5px] rounded-[1px] bg-bar-summary" />
        <div className="absolute left-0 top-0 h-[9px] w-[2px] bg-bar-summary" />
        <div className="absolute right-0 top-0 h-[9px] w-[2px] bg-bar-summary" />
      </div>
    </div>
  );
}

const REMAINING_BASIS: Array<{ item: string; policy: string }> = [
  { item: "기준점", policy: "진척 기준일(Status Date). 오늘 날짜가 아니며, 사람이 옮기기 전까지 고정" },
  { item: "단위", policy: "근무일. 주말·공휴일 제외 — 기간·여유 컬럼과 같은 단위" },
  { item: "종료일 당일", policy: "0d. 그날 안에 끝내야 한다는 뜻" },
  { item: "비근무일 기준일", policy: "직전 근무일로 당겨 계산. 금요일 종료 → 토요일 기준일은 아직 0d" },
  { item: "진척 반영", policy: "100%면 계산하지 않음. 날짜가 아무리 지나도 음수가 되지 않음" },
  { item: "단계(요약)", policy: "하위에서 롤업된 종료일·진척률로 판단" },
  { item: "적용 범위", policy: "간트 단계 브래킷 · WBS 「남은 기간」 컬럼 · 엑셀 내보내기 모두 같은 규칙" },
];

const REMAINING_STATES: Array<{ state: string; condition: string; info: RemainingInfo; color: string }> = [
  { state: "완료", condition: "진척 100%", info: { kind: "done", days: 0 }, color: "숨김 (그리드는 —)" },
  { state: "시작 전", condition: "기준일 < 시작일", info: { kind: "upcoming", days: 20 }, color: "보조색" },
  { state: "진행 중", condition: "시작일 ≤ 기준일 ≤ 종료일", info: { kind: "active", days: 12 }, color: "본문색" },
  { state: "늦어짐", condition: "기준일 > 종료일, 진척 < 100%", info: { kind: "overdue", days: -3 }, color: "크리티컬 빨강" },
];

const REMAINING_TOOLS: Array<{ tool: string; basis: string; value: string; done: string }> = [
  { tool: "MS Project", basis: "상태 날짜", value: "Remaining Duration = 기간 × (1 − 진척률)", done: "0d" },
  { tool: "Primavera P6", basis: "데이터 날짜(Data Date)", value: "Remaining Duration, 남은 작업을 데이터 날짜 뒤로 재배치", done: "0, 실적 종료일 고정" },
  { tool: "Smartsheet · monday · Asana", basis: "오늘", value: "마감까지 남은 일수, 지나면 「n일 지남」", done: "표시 안 함" },
  { tool: "Jira · Linear", basis: "오늘", value: "마감 칩, 지나면 빨강", done: "경고 해제" },
  { tool: "TeamGantt · GanttPRO", basis: "오늘", value: "숫자 없이 오늘선과 진척 채움", done: "—" },
  { tool: "Super Gantt", basis: "진척 기준일", value: "종료일까지 남은 근무일, 늦으면 음수", done: "숨김" },
];

function RemainingPolicy() {
  const styles = useStyles();
  const pStyles = usePortfolioStyles();
  return (
    <Section title="남은 기간 정책" hint="무엇을 기준으로 세고, 끝난 작업은 어떻게 보이는가">
      <Card className={styles.card}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3, lineHeight: 1.6 }}>
          음수는 「늦은 근무일 수」라는 뜻으로만 씁니다. 100% 끝난 작업은 기준일을 1년 뒤로 옮겨도 −250d 같은 값이 생기지 않고, 끝나지 못한 작업만 빨간 음수로 남습니다.
        </Caption1>
        <div className={pStyles.tableWrap}>
          <table className={pStyles.table}>
            <thead>
              <tr>
                <th className={pStyles.th}>항목</th>
                <th className={pStyles.th}>정책</th>
              </tr>
            </thead>
            <tbody>
              {REMAINING_BASIS.map((r) => (
                <tr key={r.item}>
                  <td className={pStyles.td}>{r.item}</td>
                  <td className={pStyles.td}>{r.policy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={pStyles.tableWrap}>
          <table className={pStyles.table}>
            <thead>
              <tr>
                <th className={pStyles.th}>상태</th>
                <th className={pStyles.th}>조건</th>
                <th className={mergeClasses(pStyles.th, pStyles.num)}>표시</th>
                <th className={pStyles.th}>색</th>
              </tr>
            </thead>
            <tbody>
              {REMAINING_STATES.map((r) => (
                <tr key={r.state}>
                  <td className={pStyles.td}>{r.state}</td>
                  <td className={pStyles.td}>{r.condition}</td>
                  <td className={mergeClasses(pStyles.td, pStyles.num)}>
                    {r.info.kind === "done" ? <span className="text-muted-foreground">—</span> : <RemainingLabel info={r.info} className="text-[13px]" />}
                  </td>
                  <td className={pStyles.td}>{r.color}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Card className={styles.card}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3, lineHeight: 1.6 }}>
          다른 도구 비교 — 일정 전문 도구는 상태 기준일, 업무 관리 도구는 오늘을 기준으로 삼습니다. 공통점은 완료된 작업을 0 또는 숨김으로 처리하고 음수로 두지 않는다는 것입니다.
        </Caption1>
        <div className={pStyles.tableWrap}>
          <table className={pStyles.table}>
            <thead>
              <tr>
                <th className={pStyles.th}>도구</th>
                <th className={pStyles.th}>기준점</th>
                <th className={pStyles.th}>남은 값</th>
                <th className={pStyles.th}>완료된 작업</th>
              </tr>
            </thead>
            <tbody>
              {REMAINING_TOOLS.map((r) => (
                <tr key={r.tool}>
                  <td className={pStyles.td}>{r.tool}</td>
                  <td className={pStyles.td}>{r.basis}</td>
                  <td className={pStyles.td}>{r.value}</td>
                  <td className={pStyles.td}>{r.done}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </Section>
  );
}

/** Mirrors the Gantt task bar markup (16px default row) so the showcase stays true to the chart. */
function DemoTaskBar({
  percent,
  critical = false,
  width,
  height = 16,
  showLabel = true,
}: {
  percent: number;
  critical?: boolean;
  width?: number;
  height?: number;
  showLabel?: boolean;
}) {
  const visible = showLabel && fitsProgressLabel(width ?? Infinity, height);
  return (
    <div className="relative rounded-[1px] bg-bar" style={{ width: width ?? "100%", height }}>
      <div className="absolute inset-y-0 left-0 bg-foreground/75" style={{ width: `${percent}%` }} />
      {critical && <div className="absolute inset-y-0 left-0 z-[1] w-0.5 bg-critical" />}
      {visible && <BarProgressLabel percent={percent} critical={critical} />}
    </div>
  );
}

/** Planner surfaces: status/priority badges, labels, scope chips, a task card, empty state. */
function PlannerTab() {
  const styles = useStyles();
  const [statusPick, setStatusPick] = useState("notStarted");
  const [priorityPick, setPriorityPick] = useState("medium");
  const [issueStatus, setIssueStatus] = useState("open");
  const [severityPick, setSeverityPick] = useState("high");
  const today = todayISO();
  const sample = createSampleProject();
  const schedule = computeSchedule(sample, new WorkCalendar(sample.calendar));
  const ctx: TaskQueryContext = { project: sample, schedule, today };
  const task = sample.executionTasks.find((t) => t.id === "x_gap_review")!;
  const undecidedTask = makeExecutionTask({
    id: "ds_undecided",
    title: "담당·일정·상태가 아직 없는 Task — 제목이 길어지면 한 줄로 말줄임되고 툴팁이 제공됩니다",
    notes: "만들고 나중에 정한다. 설명이 아주 길어져도 카드 전체 가로폭 100%를 쓰며 말줄임 처리되고 마우스 호버 시 전체 내용이 툴팁으로 보입니다.",
  });
  const overdueTask = { ...sample.executionTasks.find((t) => t.id === "x_interview_bug")!, due: "2026-09-01" };
  return (
    <>
      <Section title="배지·칩" hint="상태·우선순위·심각도·라벨·마감·담당·연결 범위">
        <Card className={styles.card}>
          <Caption1>상태 배지 — 아이콘과 색을 함께 써서 색만으로 구분하지 않습니다</Caption1>
          <div className={styles.row}>
            <StatusBadge status={undefined} />
            {STATUS_ORDER.map((s) => (
              <StatusBadge key={s} status={s} />
            ))}
          </div>
          <Caption1>표 셀 — 12px 표식과 본문 글자. 완료·미정·낮음 글자만 옅고, 우선순위가 없으면 —</Caption1>
          <div className={styles.row}>
            <TaskStatusChip status={undefined} />
            {STATUS_ORDER.map((status) => (
              <TaskStatusChip key={status} status={status} />
            ))}
            <TaskPriorityChip priority={undefined} />
            {PRIORITY_ORDER.map((priority) => (
              <TaskPriorityChip key={priority} priority={priority} />
            ))}
          </div>
          <Caption1>고르는 자리 — 메뉴와 서랍 셀렉트는 16px 표식과 이름. 한 작업의 동작 메뉴는 그 값의 표식과 필드 이름</Caption1>
          <div className={styles.row}>
            <span className={styles.row}>{statusGlyph(undefined, 16, true)}미정</span>
            {STATUS_ORDER.map((s) => (
              <span key={s} className={styles.row}>
                {statusGlyph(s, 16, true)}
                {STATUS_LABEL[s]}
              </span>
            ))}
            <span className={styles.row}>{priorityGlyph(undefined)}우선순위 미정</span>
            {PRIORITY_ORDER.map((p) => (
              <span key={p} className={styles.row}>
                {priorityGlyph(p)}
                {PRIORITY_LABEL[p]}
              </span>
            ))}
          </div>
          <div className={styles.row}>
            <FuiFilterSelect label="상태" value={statusPick} options={STATUS_VALUE_OPTIONS} onValueChange={setStatusPick} />
            <FuiFilterSelect label="우선순위" value={priorityPick} options={PRIORITY_VALUE_OPTIONS} onValueChange={setPriorityPick} />
          </div>
          <Caption1>우선순위 배지</Caption1>
          <div className={styles.row}>
            <PriorityBadge priority={undefined} showUnset />
            {PRIORITY_ORDER.map((p) => (
              <PriorityBadge key={p} priority={p} />
            ))}
          </div>
          <Caption1>기록 상태·심각도 — 표 셀은 12px 표식과 본문 글자. 종결된 상태와 낮음만 옅고, 심각도가 없으면 —</Caption1>
          <div className={styles.row}>
            {RECORD_STATUSES.issue.map((status) => (
              <RecordStatusBadge key={status} kind="issue" status={status} />
            ))}
            <SeverityBadge severity={undefined} />
            {SEVERITY_ORDER.map((s) => (
              <SeverityBadge key={s} severity={s} />
            ))}
          </div>
          <Caption1>서랍에서 고르는 자리 — 현재 값과 팝오버 항목은 16px 표식과 이름</Caption1>
          <div className={styles.row}>
            <FuiFilterSelect label="상태" value={issueStatus} options={recordStatusOptions("issue")} onValueChange={setIssueStatus} />
            <FuiFilterSelect label="심각도" value={severityPick} options={severityOptions()} onValueChange={setSeverityPick} />
          </div>
          <Caption1>라벨 팔레트 (9색) · 마감 칩 · 담당 아바타. 담당도 사람 아이콘이고, 남은 인원은 +N입니다.</Caption1>
          <div className={styles.row}>
            {LABEL_COLORS.map((c) => (
              <LabelChip key={c} label={{ id: c, name: LABEL_STYLE[c].name, color: c }} />
            ))}
          </div>
          <div className={styles.row}>
            <DueChip task={{ ...task, start: today, due: today }} today={today} />
            <DueChip task={{ ...task, start: undefined, due: "2026-01-05" }} today={today} />
            <DueChip task={{ ...task, status: "done", start: undefined, due: "2026-01-05" }} today={today} />
            <AssigneeAvatars names={["김PM", "박현업", "이아키", "최기획"]} />
            <AssigneeAvatars names={[]} />
          </div>
          <Caption1>연결 범위 칩 — WBS(폴더) · 작업(목록) · 마일스톤(◆). 클릭하면 일정 탭으로 점프</Caption1>
          <div className={styles.row}>
            <ScopeChips task={{ ...task, linkedTaskIds: ["p2", "t2-4", "m2", "t3-1"] }} ctx={ctx} max={4} size="small" />
          </div>
        </Card>
      </Section>
      <Section title="카드" hint="목록·보드·내 작업에서 쓰는 Task 카드">
        <div className={styles.swatchGrid} style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
          <TaskCard task={task} ctx={ctx} projectId="sample" onOpen={() => {}} onStatus={() => {}} />
          <TaskCard task={undecidedTask} ctx={ctx} projectId="sample" onOpen={() => {}} onStatus={() => {}} />
          <TaskCard task={overdueTask} ctx={ctx} projectId="sample" projectName="다른 플랜 (내 작업 카드)" onOpen={() => {}} />
        </div>
      </Section>
      <TaskMenuDemo />
      <Section title="빈 상태" hint="목록이 비면 남은 높이의 세로 가운데. 만들기는 헤더 액션으로">
        <Card className={styles.card} style={{ height: 280 }}>
          <GhostEmptyState title="아직 Task가 없습니다" description="제목을 적고 ↩ 하세요." />
        </Card>
      </Section>
    </>
  );
}

function TaskMenuDemo() {
  const styles = useStyles();
  const [status, setStatus] = useState<ExecutionStatus | undefined>("inProgress");
  const [priority, setPriority] = useState<ExecutionPriority | undefined>("important");
  const [assignees, setAssignees] = useState<string[]>(["김PM"]);
  const [note, setNote] = useState("메뉴에서 상태, 담당, 우선순위를 바꿉니다.");
  return (
    <Section title="동작 메뉴" hint="열기, 완료, 상태, 담당, 우선순위, 복제, 삭제. 상태와 우선순위를 여는 항목은 그 작업의 표식과 필드 이름입니다.">
      <Card className={styles.card}>
        <div className={styles.row}>
          <TaskCommandMenu
            task={{ id: "ds-task", title: "킥오프 참석자 확정", status, priority, assignees }}
            canWrite
            assignees={["김PM", "박현업", "이아키"]}
            onOpen={() => setNote("열기를 눌렀습니다.")}
            onStatus={setStatus}
            onPriority={setPriority}
            onAssignees={setAssignees}
            onDuplicate={() => setNote("복제했습니다.")}
            onDelete={() => setNote("삭제를 확인했습니다.")}
            trigger={<FuiButton appearance="subtle" icon={<MoreHorizontal16Regular />}>동작</FuiButton>}
          />
          <Caption1>
            {note} · {status ? STATUS_LABEL[status] : "상태 미정"} · {priority ? PRIORITY_LABEL[priority] : "우선순위 미정"} · {assignees.join(", ") || "담당 미정"}
          </Caption1>
        </div>
      </Card>
    </Section>
  );
}

function CalendarTab() {
  const styles = useStyles();
  const today = todayISO();
  const [empty, setEmpty] = useState<string | undefined>();
  const [filled, setFilled] = useState<string | undefined>(today);
  const [dialog, setDialog] = useState<string | undefined>(today);
  const [input, setInput] = useState<string | undefined>(today);
  return (
    <>
      <Section title="날짜 필드" hint="인스펙터·툴바. 페이지 28px, 비어 있으면 플레이스홀더. 클릭하면 달력이 열립니다">
        <Card className={styles.card}>
          <Field label="마감 (Deadline)">
            <DateField className={styles.dateField} value={empty} onChange={setEmpty} placeholder="마감 없음" clearable aria-label="마감" />
          </Field>
          <Field label="값 있음">
            <DateField className={styles.dateField} value={filled} onChange={setFilled} clearable aria-label="날짜" />
          </Field>
          <Field label="다이얼로그 · 24px">
            <DateField className={styles.dateField} value={dialog} onChange={setDialog} size="sm" clearable aria-label="진척 기준일" />
          </Field>
        </Card>
      </Section>
      <Section title="입력형 날짜" hint="Task 서랍. 같은 날짜 필드, 페이지와 같이 28px">
        <Card className={styles.card}>
          <Field label="바로가기">
            <DateField className={styles.dateField} presets value={input} onChange={setInput} clearable aria-label="마감일 바로가기" />
          </Field>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>오늘, 내일, 다음 주 월요일, 2주 뒤. 값이 있으면 지우기.</Caption1>
        </Card>
      </Section>
      <Section title="달력" hint="비근무일은 흐리게, 오늘은 표시. 날짜 필드가 여는 팝오버와 같습니다">
        <Card className={styles.card}>
          <DateCalendar value={filled} onSelect={setFilled} />
        </Card>
      </Section>
    </>
  );
}

const DIALOG_FORM_RULES = [
  "라벨은 입력 위(12px · 보조색), 필수는 빨간 *. 힌트는 입력 아래(12px · 연한색), 유효성 실패 시 같은 자리가 빨간 안내로 바뀝니다.",
  "가장 중요한 값은 한 줄 전체, 짝을 이루는 값은 DialogGrid2로 2열. 한 열만 남으면 빈 칸을 두고 폭을 늘리지 않습니다.",
  "켜야만 적용되는 값은 DialogToggleField. 꺼도 입력은 숨기지 않고 비활성 — 레이아웃이 튀지 않고 보존된 값이 보입니다.",
  "저장되는 옵션 체크박스는 본문에 DialogField + DialogCheckbox로. 푸터에는 두지 않습니다.",
  "푸터는 위 구분선 아래다. 왼쪽은 합계·배율 같은 실시간 요약 또는 무엇이 빠졌는지 안내, 오른쪽은 취소 + 기본 동작(24px).",
  "헤더는 제목 13px 한 줄, 위 8px·아래 4px, 구분선이다. …(목업)는 제목 바로 오른쪽이고, 기능 버튼은 닫기 왼쪽, 닫기는 오른쪽 끝 24px다. 제목 아래 설명은 두지 않는다.",
  "탭이 있는 모달은 height를 고정한다. 탭을 바꿔도 바깥 높이는 같다.",
];

function DialogFormBody({ total, setTotal, overhead, setOverhead, overheadOn, setOverheadOn, hidden, setHidden }: { total: string; setTotal: (v: string) => void; overhead: string; setOverhead: (v: string) => void; overheadOn: boolean; setOverheadOn: (v: boolean) => void; hidden: boolean; setHidden: (v: boolean) => void }) {
  const totalOk = total.trim() !== "" && Number(total) >= 0;
  return (
    <>
      <DialogField label="총 승인 사업 예산" required hint={totalOk ? undefined : <span style={{ color: tokens.colorPaletteRedForeground1 }}>0 이상으로 입력하세요</span>}>
        <FuiInput density="dialog" type="number" value={total} onChange={(_, d) => setTotal(d.value)} contentBefore="₩" aria-label="총 승인 사업 예산" aria-invalid={!totalOk} />
      </DialogField>
      <DialogGrid2>
        <DialogField label="목표 인건비">
          <FuiInput density="dialog" type="number" defaultValue="320000000" contentBefore="₩" aria-label="목표 인건비" />
        </DialogField>
        <DialogField label="목표 솔루션비">
          <FuiInput density="dialog" type="number" defaultValue="120000000" contentBefore="₩" aria-label="목표 솔루션비" />
        </DialogField>
      </DialogGrid2>
      <DialogGrid2>
        <DialogToggleField label="제경비(Overhead) 포함" checked={overheadOn} onCheckedChange={setOverheadOn} hint="직접인건비 대비">
          <FuiInput density="dialog" type="number" value={overhead} onChange={(_, d) => setOverhead(d.value)} contentAfter="%" disabled={!overheadOn} aria-label="제경비율" />
        </DialogToggleField>
        <DialogField label="1 M/M 기준 근무일수" hint="SW 대가산정 표준 20.83일">
          <FuiInput density="dialog" type="number" defaultValue="21" contentAfter="일" aria-label="1 M/M 기준 근무일수" />
        </DialogField>
      </DialogGrid2>
      <DialogField label="공개 범위">
        <DialogCheckbox label="일반 팀원에게 단가·금액 기본 비공개" checked={hidden} onChange={setHidden} />
      </DialogField>
    </>
  );
}

function DialogChromeSample({ title }: { title: string }) {
  const styles = useStyles();
  return (
    <div className={styles.dialogChrome}>
      <div className={styles.dialogChromeTitleRow}>
        <div className={styles.dialogChromeTitle}>{title}</div>
        <span className={styles.dialogChromeBtn} aria-hidden>
          <MoreHorizontal16Regular />
        </span>
      </div>
      <div className={styles.dialogChromeActions}>
        <span className={styles.dialogChromeBtn} aria-hidden>
          <Dismiss16Regular />
        </span>
      </div>
    </div>
  );
}

function DialogFormDemo() {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [total, setTotal] = useState("580000000");
  const [overhead, setOverhead] = useState("10");
  const [overheadOn, setOverheadOn] = useState(true);
  const [hidden, setHidden] = useState(false);
  const body = { total, setTotal, overhead, setOverhead, overheadOn, setOverheadOn, hidden, setHidden };
  const valid = total.trim() !== "" && Number(total) >= 0;
  const summary = valid ? <Caption1>목표 합계 ₩440,000,000 · 미배정 ₩140,000,000</Caption1> : <Caption1 style={{ color: tokens.colorPaletteRedForeground1 }}>총 승인 사업 예산 입력이 필요합니다</Caption1>;

  return (
    <Card className={styles.card}>
      <div className={styles.dialogFormLayout}>
        <div className={styles.dialogFormPreview} aria-label="다이얼로그 폼 미리보기">
          <DialogChromeSample title="예산안 변경" />
          <div style={{ display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS }}>
            <DialogFormBody {...body} />
          </div>
          <div className={styles.dialogChromeFooter}>
            {summary}
            <div style={{ display: "flex", gap: tokens.spacingHorizontalXS }}>
              <FuiButton density="dialog" appearance="secondary">취소</FuiButton>
              <FuiButton density="dialog" appearance="primary" disabled={!valid}>저장</FuiButton>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS, minWidth: 0, flex: 1 }}>
          <ul style={{ margin: 0, paddingLeft: tokens.spacingHorizontalL, listStyleType: "disc", display: "flex", flexDirection: "column", gap: tokens.spacingVerticalXS }}>
            {DIALOG_FORM_RULES.map((r) => (
              <li key={r}>
                <Caption1>{r}</Caption1>
              </li>
            ))}
          </ul>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>컴포넌트: AppDialog · DialogField · DialogGrid2 · DialogToggleField · DialogCheckbox · DialogActionsBar (@/components/app-dialog)</Caption1>
          <div>
            <FuiButton appearance="outline" onClick={() => setOpen(true)}>
              실제 다이얼로그로 열기
            </FuiButton>
          </div>
        </div>
      </div>
      <AppDialog
        open={open}
        onClose={() => setOpen(false)}
        width="md"
        title="예산안 변경"
        titleAside={<MockCaseMenu cases={[{ id: "sample", label: "예시" }]} onPick={() => undefined} />}
        footerStart={summary}
        footer={<DialogActionsBar onCancel={() => setOpen(false)} onConfirm={() => setOpen(false)} disabled={!valid} />}
      >
        <DialogFormBody {...body} />
      </AppDialog>
    </Card>
  );
}

const INVITE_RULES = [
  "이름을 치면 입력칸 아래에 검색이 뜨고, 고르면 이번 목록에 들어갑니다.",
  "목록의 사람은 모두 권한을 바꿀 수 있습니다. 상태 글자는 두지 않습니다.",
  "줄의 빼기는 오른쪽 … 메뉴입니다.",
  "이미 이번 목록에 있는 사람은 검색에서 다시 고를 수 없습니다.",
  "링크 복사는 편집 권한입니다. 샘플이라 실제로 전송하지 않습니다.",
];

function InviteDemo() {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [filled, setFilled] = useState(true);
  return (
    <Card className={styles.card}>
      <SegmentedControl
        ariaLabel="초대 표본"
        value={filled ? "filled" : "empty"}
        onChange={(value) => setFilled(value === "filled")}
        options={[
          { value: "filled", label: "사람 있음" },
          { value: "empty", label: "아직 없음" },
        ]}
      />
      <div className={styles.dialogFormLayout}>
        <div className={styles.dialogFormPreview} aria-label="초대 미리보기">
          <DialogChromeSample title="WaveX 초대" />
          <InvitePanel
            key={filled ? "filled" : "empty"}
            workspaceName="WaveX"
            empty={!filled}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS, minWidth: 0, flex: 1 }}>
          <ul style={{ margin: 0, paddingLeft: tokens.spacingHorizontalL, listStyleType: "disc", display: "flex", flexDirection: "column", gap: tokens.spacingVerticalXS }}>
            {INVITE_RULES.map((rule) => (
              <li key={rule}>
                <Caption1>{rule}</Caption1>
              </li>
            ))}
          </ul>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>컴포넌트: InviteDialog · InvitePanel (@/components/invite-dialog)</Caption1>
          <div>
            <FuiButton appearance="outline" onClick={() => setOpen(true)}>
              실제 다이얼로그로 열기
            </FuiButton>
          </div>
        </div>
      </div>
      <InviteDialog
        open={open}
        onClose={() => setOpen(false)}
        workspaceName="WaveX"
        empty={!filled}
      />
    </Card>
  );
}

/* ------------------------------------------------------------------ 날짜 포맷 */

const DATE_SAMPLE = "2026-09-05";
const DATE_SAMPLE_END = "2026-09-18";
const DATE_SAMPLE_TS = "2026-09-05T14:30:00";

const DATE_RULES: Array<{ where: string; pattern: string; example: string }> = [
  { where: "표 셀 · KPI 값 · 변경 이력 · Excel", pattern: "numeric", example: formatDate(DATE_SAMPLE, "numeric") },
  { where: "간트 바 라벨 · 마커 · 눈금 · 차트 축 · 좁은 패널", pattern: "short", example: formatDate(DATE_SAMPLE, "short") },
  { where: "문장 · 속성 값 · 날짜 입력 필드 · 툴팁 문장", pattern: "full", example: formatDate(DATE_SAMPLE, "full") },
  { where: "요일이 판단에 필요한 속성 (시작·종료)", pattern: "fullWeekday", example: formatDate(DATE_SAMPLE, "fullWeekday") },
  { where: "월 머리글 · 달력 제목", pattern: "yearMonth", example: formatDate(DATE_SAMPLE, "yearMonth") },
  { where: "월 단위 매트릭스 열", pattern: "yearMonthShort", example: formatDate(DATE_SAMPLE, "yearMonthShort") },
  { where: "기간", pattern: "formatDateRange", example: formatDateRange(DATE_SAMPLE, DATE_SAMPLE_END, "short") },
  { where: "공휴일 연휴 (같은 달이면 월은 한 번)", pattern: "formatMonthDaySpan", example: formatMonthDaySpan("2026-02-16", "2026-02-18") },
  { where: "지난 시각 (댓글·서랍 활동)", pattern: "formatAgo", example: formatAgo(new Date(Date.now() - 3 * 86_400_000)) },
  { where: "활동 목록의 시각", pattern: "clock", example: formatDate(DATE_SAMPLE_TS, "clock") },
  { where: "수정·첨부 시각", pattern: "dateTime", example: formatDate(DATE_SAMPLE_TS, "dateTime") },
  { where: "만료까지 남은 날", pattern: "formatDaysUntil", example: formatDaysUntil("2026-09-08", "2026-09-05") },
];

const DATE_DONTS = [
  "`${m}/${d}`, `${y}년 ${m}월` 처럼 문자열을 직접 조립하지 않습니다. 쓰임새 이름을 고르고, 없으면 date-format.ts에 추가합니다.",
  "toLocaleDateString · Intl.DateTimeFormat · date-fns format을 화면 코드에서 직접 부르지 않습니다. 로케일 전환이 한 곳에서 끝나야 합니다.",
  "ISO 원문(2026-09-05T05:30:00.000Z)이나 iso.slice(5)를 화면에 그대로 내보내지 않습니다.",
  "같은 화면의 같은 종류 값은 한 패턴만 씁니다. 표에서 어떤 행은 09/05, 어떤 행은 2026-09-05로 섞지 않습니다.",
  "상대 시각 문구(「3시간 전」「3일 전」)는 date-fns가 주는 그대로 쓰되, 앞의 「약」은 뺍니다. 「방금 전」 같은 자체 문구를 덧대지 않습니다.",
];

function DateFormatTab() {
  const styles = useStyles();
  const pStyles = usePortfolioStyles();
  const today = todayISO();
  return (
    <>
      <Section title="날짜 패턴" hint="화면은 쓰임새 이름만 고르고, 모양은 로케일이 정합니다. .NET 표준 서식(d·D·M·Y·t·g)과 같은 방식 · @/lib/date-format">
        <Card className={styles.card}>
          <div className={pStyles.tableWrap}>
            <table className={pStyles.table}>
              <thead>
                <tr>
                  <th className={pStyles.th}>이름</th>
                  <th className={pStyles.th}>한국어</th>
                  <th className={pStyles.th}>English</th>
                  <th className={pStyles.th}>패턴 (ko)</th>
                  <th className={pStyles.th}>쓰는 곳</th>
                </tr>
              </thead>
              <tbody>
                {DATE_PATTERN_SPECS.map(({ pattern, use }) => {
                  const value = /time$/i.test(pattern) ? DATE_SAMPLE_TS : DATE_SAMPLE;
                  return (
                    <tr key={pattern}>
                      <td className={pStyles.td}>
                        <code>{pattern}</code>
                      </td>
                      <td className={mergeClasses(pStyles.td, pStyles.num)} style={{ textAlign: "left" }}>
                        {formatDate(value, pattern, { locale: "ko" })}
                      </td>
                      <td className={mergeClasses(pStyles.td, pStyles.num)} style={{ textAlign: "left", color: tokens.colorNeutralForeground2 }}>
                        {formatDate(value, pattern, { locale: "en" })}
                      </td>
                      <td className={pStyles.td} style={{ color: tokens.colorNeutralForeground3 }}>
                        <code>{datePattern(pattern, "ko")}</code>
                      </td>
                      <td className={pStyles.td}>{use}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </Section>

      <Section title="어디에 무엇을" hint="같은 날짜라도 자리마다 한 가지로만 씁니다">
        <Card className={styles.card}>
          <div className={pStyles.tableWrap}>
            <table className={pStyles.table}>
              <thead>
                <tr>
                  <th className={pStyles.th}>자리</th>
                  <th className={pStyles.th}>쓰는 것</th>
                  <th className={pStyles.th}>예</th>
                </tr>
              </thead>
              <tbody>
                {DATE_RULES.map((r) => (
                  <tr key={r.where}>
                    <td className={pStyles.td}>{r.where}</td>
                    <td className={pStyles.td}>
                      <code>{r.pattern}</code>
                    </td>
                    <td className={mergeClasses(pStyles.td, pStyles.num)} style={{ textAlign: "left" }}>
                      {r.example}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </Section>

      <Section title="기간 · 상대 시각 · 이름" hint="구분자·문구·요일 이름도 로케일에서 옵니다">
        <Card className={styles.card}>
          <Field label="기간 (formatDateRange)">
            <Body1>
              {formatDateRange(DATE_SAMPLE, DATE_SAMPLE_END, "short")} · {formatDateRange(DATE_SAMPLE, "2027-03-31", "numeric")} · {formatDateRange(DATE_SAMPLE, DATE_SAMPLE_END, "short", { locale: "en" })}
              {" · "}
              {formatMonthDaySpan("2026-02-16", "2026-02-18")} · {formatMonthDaySpan("2026-02-16", "2026-02-18", { locale: "en" })}
            </Body1>
          </Field>
          <Field label="상대 시각 (formatAgo · 「약」은 뺌)">
            <Body1>
              {[5 * 60_000, 3 * 3_600_000, 3 * 86_400_000, 40 * 86_400_000].map((ms) => formatAgo(new Date(Date.now() - ms))).join(" · ")}
            </Body1>
          </Field>
          <Field label="날짜 그룹 머리글 (formatDayHeading)">
            <Body1>
              {[today, formatDate(new Date(Date.now() - 86_400_000), "numeric"), DATE_SAMPLE, "2025-08-01"].map((d) => formatDayHeading(d)).join(" · ")}
            </Body1>
          </Field>
          <Field label="요일 (weekdayNames)">
            <Body1>
              {weekdayNames("short").join(" ")} · {weekdayNames("short", "en").join(" ")}
            </Body1>
          </Field>
        </Card>
      </Section>

      <Section title="하지 않는 것" hint="코드 리뷰에서 걸러야 할 패턴">
        <Card className={styles.card}>
          <ul style={{ margin: 0, paddingLeft: tokens.spacingHorizontalL, display: "flex", flexDirection: "column", gap: tokens.spacingVerticalXS }}>
            {DATE_DONTS.map((t) => (
              <li key={t}>
                <Body1>{t}</Body1>
              </li>
            ))}
          </ul>
        </Card>
      </Section>
    </>
  );
}

function Section({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  const styles = useStyles();
  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <SectionTitle>{title}</SectionTitle>
        <Caption1>{hint}</Caption1>
      </div>
      {children}
    </section>
  );
}

function IconTile({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <Card>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <span className="flex size-7 items-center justify-center text-foreground">{icon}</span>
        <Caption1>{label}</Caption1>
      </div>
    </Card>
  );
}

function Metaphor({ label, hint, children }: { label: string; hint: string; children: ReactNode }) {
  return (
    <Card>
      <div className="flex flex-col gap-2 px-3 py-2.5">
        <div className="flex h-7 items-center">{children}</div>
        <div>
          <Caption1>{label}</Caption1>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{hint}</Caption1>
        </div>
      </div>
    </Card>
  );
}

function ColorTile({ name, className }: { name: string; className: string }) {
  return (
    <Card>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <span className={cn("size-7 shrink-0 rounded-md border border-border", className)} />
        <Caption1>{name}</Caption1>
      </div>
    </Card>
  );
}
